import { Injectable, OnDestroy } from '@angular/core';

import { TranslocoService } from '@jsverse/transloco';
import moment from 'moment';
import { Moment } from 'moment';
import { Subject, takeUntil } from 'rxjs';

import { Constants, RelativeDateConstants } from 'app/constants/constants';

import { SelectedSection } from 'app/modules/widget/components/section-selector/section-selector.component';
import { WidgetChartOptions } from 'app/modules/widget/components/widget-chart/widget-chart.component';
import { isAnalyticItemTextualSize } from 'app/modules/widget/components/widget-filter/widget-filter.component';
import { WidgetGroup, Widget, SectionFilter } from 'app/providers/bridge-api/dashboard.service';
import { SectionMetadataType } from 'app/providers/storage-api/collection.service';
import { FieldService } from 'app/providers/storage-api/field.service';
import { DateRangeService } from 'app/services/date-range.service';
import { EnableClassTelemetry } from 'app/services/otel.service';
import { TimezoneService } from 'app/services/timezone.service';
import { FilterUtils } from 'app/utils/filter.utils';
import { Utils } from 'app/utils/utils';
import { WidgetDataSource } from '../../../models';
import { LexDatePipe } from '../../../pipes/lex-date.pipe';

export interface NumericFilterDisplayValue {
  leftOperand: {
    operator: string;
    value: string;
  };
  booleanOperator?: 'AND' | 'OR';
  rightOperand?: {
    operator: string;
    value: string;
  };
}

export interface FilterConfigChoice {
  key?: string;
  value: string | number;
  picker?: string;
  label: string;
}

export interface BoxScoringQueryOptions {
  fieldId: string;
  min: number;
  max: number;
  topBoxSize: string;
  bottomBoxSize: string;
  type: 'donut' | 'breakdown';
}

export interface BucketScoringQueryOptions {
  fieldId: string;
  minimumValue: number;
  maximumValue: number;
  includeMinimum: boolean;
  includeMaximum: boolean;
}

export interface WidgetQueryOptions {
  widgetContextMenu?: any;
  pointName?: string;
  analyticItem: string;
  subType?: string;
  parentAnalyticItem?: string;
  parentPointName?: string;
  parentIsMetadata?: boolean;
  parentSubType?: string;
  parentSectionPropertySelected?: boolean;
  parentSelectedSections?: SelectedSection[];
  type:
    | 'documentCount'
    | 'numericMetadata'
    | 'sentimentScore'
    | 'sentimentPolarity'
    | 'sentimentVolume'
    | 'taxonomyMap'
    | 'topMovers'
    | 'wordcloud'
    | 'crosstab'
    | 'featureScoring'
    | 'scatterPlot'
    | 'sentimentAccuracy'
    | 'gauge'
    | 'grid'
    | 'journeyGroup';
  drilledDownOn:
    | 'themes'
    | 'mentions'
    | 'taxonomy'
    | 'taxonomy_mentions'
    | 'topic_phrases'
    | 'entity_phrases'
    | 'taxonomy_phrases'
    | 'taxonomy_themes'
    | 'none';
  analyticItems?: string[];
  taxonomyPath?: string;
  taxonomyMentionField?: string;
  drilledDownLocation?: string;
  widgetDataSource: WidgetDataSource;
  customFieldSelected: boolean;
  standardFieldSelected: boolean;
  sectionPropertySelected?: boolean;
  filters: AnalyticItemFilterSets;
  sentimentScale: number;
  selectedItems: string[];
  dateInterval?: string;
  userQuery?: string;
  subject?: string;
  subjectValue?: string;
  userSearch?: any;
  boxScoringQueryOptions?: BoxScoringQueryOptions;
  bucketScoringQueryOptions?: BucketScoringQueryOptions;
  selectedSection?: SelectedSection[];
  sectionFilter?: SectionFilter[];
  contentChannelFilter?: string[];
}

export interface FilterConfig {
  title: string;
  filterSet: string;
  excludeEmpty?: boolean;
  choices?: FilterConfigChoice[];
  nested?: string;
  isMultiLevel?: boolean;
  fieldName?: string;
  whenAnalyticItem?: string;
  computeValue?: (v: any) => string;
  computeLabel?: (v: any) => string;
  coreField?: boolean;
  type: string;
  shortLabel: string;
}

export interface Filter {
  key?: string;
  analyticItem: string;
  value: string;
  subLabel?: string;
  hidden?: boolean;
  displayName?: string;
  parentFilter?: boolean;
  analyticItemShortLabel?: string;
  displayValue?: string;
  displayTitle?: string;
  taxonomyPath?: string;
  fromWidgetGroup?: boolean;
  fromWidgetGroupParent?: boolean;
  sectionField?: string;
  sectionBuckets?: string[];
  selectedSections?: SelectedSection[];
  kind?: FilterKind;
}

/**
 * Describes how a filter value should be interpreted when building the search query.
 * - comparisonSectionFilter: Comparison widget filter that scopes to a specific section.
 * - pointFilter: Point-driven filter when the user clicks on a point in a chart.
 */
export type FilterKind = 'comparisonSectionFilter' | 'pointFilter';

export interface FilterSet {
  and: Filter[];
  or: Filter[];
  not: Filter[];
  label?: string;
  id?: string;
}

export type OperatorType = 'and' | 'or' | 'not';

export const DATE_WITHOUT_TIMEZONE_FORMAT = 'YYYY-MM-DD';
export const DATE_TIME_WITHOUT_TIMEZONE_FORMAT = 'YYYY-MM-DD[T]HH:mm:ss.SSS';

export const dateFilterChoices = [
  { key: RelativeDateConstants.TODAY, value: '[now/d TO now+1d/d}', label: 'Today', query: '' },
  {
    key: RelativeDateConstants.YESTERDAY,
    value: '[now-1d/d TO now/d}',
    label: 'Yesterday',
    query: '',
  },
  {
    key: RelativeDateConstants.LAST_7_DAY,
    value: '[now-7d/d TO now/d}',
    label: 'Last 7 Days',
    query: '',
  },
  {
    key: RelativeDateConstants.LAST_7_DAY_INCLUDE_TODAY,
    value: '[now-6d/d TO now+1d/d}',
    label: 'Last 7 Days (including today)',
    query: '',
  },
  {
    key: RelativeDateConstants.PREV_7_DAY,
    value: '[now-14d/d TO now-7d/d}',
    label: 'Previous 7 Days',
    query: '',
  },
  {
    key: RelativeDateConstants.PREV_7_DAY_INCLUDE_TODAY,
    value: '[now-13d/d TO now-6d/d}',
    label: 'Previous 7 Days (counting from today inclusive)',
    query: '',
  },
  {
    key: RelativeDateConstants.LAST_30_DAY,
    value: '[now-30d/d TO now/d}',
    label: 'Last 30 Days',
    query: '',
  },
  {
    key: RelativeDateConstants.LAST_30_DAY_INCLUDE_TODAY,
    value: '[now-29d/d TO now+1d/d}',
    label: 'Last 30 Days (including today)',
    query: '',
  },
  {
    key: RelativeDateConstants.PREV_30_DAY,
    value: '[now-60d/d TO now-30d/d}',
    label: 'Previous 30 Days',
    query: '',
  },
  {
    key: RelativeDateConstants.PREV_30_DAY_INCLUDE_TODAY,
    value: '[now-59d/d TO now-29d/d}',
    label: 'Previous 30 Days (counting from today inclusive)',
    query: '',
  },
  {
    key: RelativeDateConstants.MONTH_TO_DATE,
    value: '[now/M TO now/d}',
    label: 'Month to Date',
    query: '',
  },
  {
    key: RelativeDateConstants.MONTH_TO_DATE_INCLUDE_TODAY,
    value: '[now/M TO now+1d/d}',
    label: 'Month to Date (including today)',
    query: '',
  },
  {
    key: RelativeDateConstants.PREV_MONTH,
    value: '[now-1M/M TO now/M}',
    label: 'Previous Month',
    query: '',
  },
  {
    key: RelativeDateConstants.PREV_YEAR,
    value: '[now-1y/y TO now/y}',
    label: 'Previous year',
    query: '',
  },
  {
    key: RelativeDateConstants.YEAR_TO_DATE,
    value: '[now/y TO now/d}',
    label: 'Year to date',
    query: '',
  },
  {
    key: RelativeDateConstants.YEAR_TO_DATE_INCLUDE_TODAY,
    value: '[now/y TO now+1d/d}',
    label: 'Year to date (including today)',
    query: '',
  },
  { key: 'daterange', value: 'daterange', label: 'Fixed', picker: 'daterange' },
];

const itemFilterSetsInValue = {
  topics: 'analyses.topics',
  entities: 'analyses.entities',
  themes: 'analyses.themes',
  phrases: 'analyses.phrases',
  taxonomy: 'analyses.taxonomy',
  metadata: 'metadata',
  analyses: 'analyses',
};

const analysisFields = [
  'analyses.topics.sentimentPolarity',
  'analyses.entities.sentimentPolarity',
  'analyses.phrases.sentimentPolarity',
  'analyses.taxonomy.sentimentPolarity',
  'analyses.themes.sentimentPolarity',
  'analyses.sentimentPolarity',
  'ML-analyses.eie.title',
  'ML-analyses.topics.title',
  'ML-analyses.entities.title',
  'analyses.phrases.title',
  'analyses.themes.normalizedTitle',
];

export interface SentimentFilterPolarity {
  value: string;
  checked: boolean;
}

export interface AnalyticItemFilterSets {
  topics: FilterSet;
  entities: FilterSet;
  themes: FilterSet;
  phrases: FilterSet;
  dates: FilterSet;
  analyses: FilterSet;
  metadata: FilterSet;
  tags: FilterSet;
  core: FilterSet;
  taxonomy: FilterSet;
  sectionProperties: FilterSet;
}

export interface UserSearchFilter {
  userSearch: FilterSet;
}

export interface SentimentFilterEventsManager {
  events: any;
  shouldBeVisible: (value: string, isTotalValue?: boolean) => boolean;
}

export interface FilterPillTexts {
  text: string;
  textExtra: string;
  shortLabel: string;
}

function escapeQuotes(s: string): string {
  if (!s) {
    return '';
  }
  return s.replace(/"/g, '\\"');
}

export function getSectionFilters(sets: AnalyticItemFilterSets | UserSearchFilter): Filter[] {
  const results: Filter[] = [];

  if (!sets) {
    return results;
  }

  for (const analyticItemKey of Object.keys(sets)) {
    if (sets[analyticItemKey]['and']) {
      for (const filter of sets[analyticItemKey]['and']) {
        if (filter.sectionField || filter.selectedSections?.length) {
          results.push(filter);
        }
      }
    }
  }

  return results;
}

@EnableClassTelemetry()
@Injectable()
export class FilterService implements OnDestroy {
  private unsub$ = new Subject<void>();

  constructor(
    private lexDatePipe: LexDatePipe,
    private timezoneService: TimezoneService,
    private translocoService: TranslocoService,
    private dateRangeService: DateRangeService
  ) {
    this.translocoService
      .selectTranslateObject('filters.titles')
      .pipe(takeUntil(this.unsub$))
      .subscribe((translations) => {
        for (const filterKey of Object.keys(this.filterConfig)) {
          this.filterConfig[filterKey].title = translations[filterKey.replace(/\./g, '_')];
        }
      });

    this.translocoService
      .selectTranslateObject('filters.date-titles')
      .pipe(takeUntil(this.unsub$))
      .subscribe((translations) => {
        for (const filter of dateFilterChoices) {
          filter.label = translations[filter.key] ?? filter.label;
        }
      });
  }

  ngOnDestroy(): void {
    this.unsub$.next();
    this.unsub$.complete();
  }

  public static readonly analysisFields = analysisFields;

  public static readonly standardFields = [
    'creator',
    'language',
    'locationId',
    'parentId',
    'publisher',
    'title',
    'source',
    'tags',
    'type',
    'url',
  ];

  public static readonly clauseModelTypes = ['Intent', 'Effort', 'Emotion'];

  public readonly filterConfig: { [analyticItem: string]: FilterConfig } = {
    createdAt: {
      title: 'Creation Date',
      choices: dateFilterChoices,
      filterSet: 'dates',
      computeValue: (key) => FilterUtils.computeValueForDateFilter(key.toString()),
      computeLabel: (value) => this.computeLabelForDateFilter(value.toString()),
      type: 'date',
      shortLabel: 'MD',
    },
    'analyses.topics.sentimentPolarity': {
      title: 'Topic-Level Sentiment',
      excludeEmpty: true,
      nested: 'analyses.topics',
      whenAnalyticItem: 'topics',
      filterSet: 'topics',
      fieldName: 'analyses.topics',
      type: 'checkbox',
      shortLabel: 'T',
    },
    'analyses.entities.sentimentPolarity': {
      title: 'Entity-Level Sentiment',
      excludeEmpty: true,
      nested: 'analyses.entities',
      whenAnalyticItem: 'entities',
      filterSet: 'entities',
      fieldName: 'analyses.entities',
      type: 'checkbox',
      shortLabel: 'E',
    },
    'analyses.phrases.sentimentPolarity': {
      title: 'Phrase-Level Sentiment',
      excludeEmpty: true,
      nested: 'analyses.phrases',
      whenAnalyticItem: 'phrases',
      filterSet: 'phrases',
      fieldName: 'analyses.phrases',
      type: 'checkbox',
      shortLabel: 'Pp',
    },
    'analyses.taxonomy.sentimentPolarity': {
      title: 'Taxonomy-Level Sentiment',
      excludeEmpty: true,
      nested: 'analyses.taxonomy',
      whenAnalyticItem: 'taxonomy',
      filterSet: 'taxonomy',
      fieldName: 'analyses.taxonomy',
      type: 'checkbox',
      shortLabel: 'Tx',
    },
    'analyses.themes.sentimentPolarity': {
      title: 'Theme-Level Sentiment',
      excludeEmpty: true,
      nested: 'analyses.themes',
      whenAnalyticItem: 'themes',
      filterSet: 'themes',
      fieldName: 'analyses.themes',
      type: 'checkbox',
      shortLabel: 'Th',
    },
    'analyses.sentimentPolarity': {
      title: 'Sentiment',
      excludeEmpty: true,
      fieldName: 'analyses',
      filterSet: 'analyses',
      type: 'checkbox',
      shortLabel: 'DS',
    },
    'ML-analyses.topics.title': {
      title: 'Topics',
      fieldName: 'analyses.topics',
      filterSet: 'topics',
      isMultiLevel: true,
      type: 'checkbox',
      shortLabel: 'T',
    },
    'ML-analyses.eie.title': {
      title: 'Clause Models',
      fieldName: 'analyses.topics',
      filterSet: 'topics',
      isMultiLevel: true,
      type: 'checkbox',
      shortLabel: 'CM',
    },
    'analyses.topics.title': {
      title: 'Topics',
      fieldName: 'analyses.topics',
      filterSet: 'topics',
      type: 'checkbox',
      shortLabel: 'T',
    },
    'ML-analyses.entities.title': {
      title: 'Entities',
      fieldName: 'analyses.entities',
      filterSet: 'entities',
      isMultiLevel: true,
      type: 'checkbox',
      shortLabel: 'E',
    },
    'analyses.entities.title': {
      title: 'Entities',
      filterSet: 'entities',
      fieldName: 'analyses.entities',
      type: 'checkbox',
      shortLabel: 'E',
    },
    'analyses.phrases.title': {
      title: 'Phrases',
      filterSet: 'phrases',
      fieldName: 'analyses.phrases',
      type: 'checkbox',
      shortLabel: 'P',
    },
    'analyses.taxonomy.taxonomyFullPath': {
      title: 'Taxonomy',
      fieldName: 'analyses.taxonomy',
      filterSet: 'taxonomy',
      type: 'checkbox',
      shortLabel: 'Tx',
    },
    'analyses.themes.normalizedTitle': {
      title: 'Themes',
      filterSet: 'themes',
      fieldName: 'analyses.themes',
      type: 'checkbox',
      shortLabel: 'Th',
    },
    creator: {
      title: 'Creator',
      filterSet: 'core',
      type: 'checkbox',
      shortLabel: 'Cr',
    },
    source: {
      title: 'Source',
      filterSet: 'core',
      type: 'checkbox',
      shortLabel: 'Src',
    },
    publisher: {
      title: 'Publisher',
      filterSet: 'core',
      type: 'checkbox',
      shortLabel: 'Pb',
    },
    language: {
      title: 'Language',
      filterSet: 'core',
      type: 'checkbox',
      shortLabel: 'Ln',
    },
    type: {
      title: 'Type',
      filterSet: 'core',
      type: 'checkbox',
      shortLabel: 'Ty',
    },
    title: {
      title: 'Title',
      filterSet: 'core',
      type: 'checkbox',
      shortLabel: 'Ti',
    },
    tags: {
      title: 'Tags',
      filterSet: 'tags',
      type: 'checkbox',
      shortLabel: 'Tg',
    },
    taxonomy: {
      title: 'Taxonomy',
      filterSet: 'taxonomy',
      type: 'checkbox',
      shortLabel: 'Tx',
    },
    userSearch: {
      title: 'User Search',
      filterSet: 'userSearch',
      type: 'checkbox',
      shortLabel: 'Us',
      computeValue: (v) => v,
    },
    'sectionProperties.segment': {
      title: 'Section Property: Segment',
      filterSet: 'sectionProperties',
      fieldName: 'analyses.flatSections',
      nested: 'analyses.flatSections',
      type: 'checkbox',
      shortLabel: 'SP',
    },
    'sectionProperties.speakerName': {
      title: 'Section Property: Speaker Name',
      filterSet: 'sectionProperties',
      fieldName: 'analyses.flatSections',
      nested: 'analyses.flatSections',
      type: 'checkbox',
      shortLabel: 'SP',
    },
    'sectionProperties.speakerType': {
      title: 'Section Property: Speaker Type',
      filterSet: 'sectionProperties',
      fieldName: 'analyses.flatSections',
      nested: 'analyses.flatSections',
      type: 'checkbox',
      shortLabel: 'SP',
    },
    'sectionProperties.sectionLabel': {
      title: 'Section Property: Section Label',
      filterSet: 'sectionProperties',
      fieldName: 'analyses.flatSections',
      nested: 'analyses.flatSections',
      type: 'checkbox',
      shortLabel: 'SP',
    },
    'sectionProperties.aliases': {
      title: 'Section Property: Aliases',
      filterSet: 'sectionProperties',
      fieldName: 'analyses.flatSections',
      nested: 'analyses.flatSections',
      type: 'checkbox',
      shortLabel: 'SP',
    },
    'sectionProperties.name': {
      title: 'Section Property: Name',
      filterSet: 'sectionProperties',
      fieldName: 'analyses.flatSections',
      nested: 'analyses.flatSections',
      type: 'checkbox',
      shortLabel: 'SP',
    },
    textualSize: {
      title: 'Text Length',
      filterSet: 'metadata',
      type: 'long',
      shortLabel: 'Tl',
    },
    url: {
      title: 'Url',
      filterSet: 'core',
      type: 'checkbox',
      shortLabel: 'URL',
    },
    parentId: {
      title: 'Parent',
      filterSet: 'core',
      type: 'checkbox',
      shortLabel: 'Pr',
    },
    locationId: {
      title: 'Location ID',
      filterSet: 'metadata',
      type: 'checkbox',
      shortLabel: 'Loc',
    },
  };

  public static getDefaultFilterSet(): AnalyticItemFilterSets {
    return {
      topics: { and: [], or: [], not: [] },
      entities: { and: [], or: [], not: [] },
      themes: { and: [], or: [], not: [] },
      phrases: { and: [], or: [], not: [] },
      dates: { and: [], or: [], not: [] },
      analyses: { and: [], or: [], not: [] },
      metadata: { and: [], or: [], not: [] },
      tags: { and: [], or: [], not: [] },
      core: { and: [], or: [], not: [] },
      taxonomy: { and: [], or: [], not: [] },
      sectionProperties: { and: [], or: [], not: [] },
    };
  }

  public static transformAnalyticFeaturePolarityFilter5(
    filterSet: FilterSet,
    analyticItem: string,
    filterGroup = 'or'
  ): void {
    if (!filterSet) {
      return;
    }

    const originalItem = `${analyticItem}3`;
    const transformedItem = `${analyticItem}5`;

    for (let i = filterSet[filterGroup].length - 1; i > -1; i--) {
      if (filterSet[filterGroup][i].analyticItem === originalItem) {
        const newFilters = [];
        switch (filterSet[filterGroup][i].value) {
          case 'positive':
            newFilters.push({
              ...filterSet[filterGroup][i],
              analyticItem: transformedItem,
              displayValue: 'very positive',
              displayName: 'very positive',
              value: 'very positive',
            });
            if (filterGroup !== 'and') {
              newFilters.push({
                ...filterSet[filterGroup][i],
                analyticItem: transformedItem,
                displayValue: 'somewhat positive',
                displayName: 'somewhat positive',
                value: 'somewhat positive',
              });
            }

            break;
          case 'negative':
            newFilters.push({
              ...filterSet[filterGroup][i],
              analyticItem: transformedItem,
              displayValue: 'very negative',
              displayName: 'very negative',
              value: 'very negative',
            });
            if (filterGroup !== 'and') {
              newFilters.push({
                ...filterSet[filterGroup][i],
                analyticItem: transformedItem,
                displayValue: 'somewhat negative',
                displayName: 'somewhat negative',
                value: 'somewhat negative',
              });
            }

            break;
          default:
            newFilters.push({ ...filterSet[filterGroup][i], analyticItem: transformedItem });
        }
        filterSet[filterGroup].splice(i, 1, ...newFilters);
      }
    }
  }

  public static transformDocumentPolarityFilter5(filterSet: FilterSet, analyticItem: string): void {
    FilterService.transformAnalyticFeaturePolarityFilter5(filterSet, analyticItem, 'and');
    FilterService.transformAnalyticFeaturePolarityFilter5(filterSet, analyticItem, 'or');
    FilterService.transformAnalyticFeaturePolarityFilter5(filterSet, analyticItem, 'not');
  }

  public static transformAnalyticFeaturePolarityFilter3(
    filterSet: FilterSet,
    analyticItem: string,
    filterGroup = 'or',
    fromDocument = false
  ): void {
    if (!filterSet) {
      return;
    }

    const originalItem = `${analyticItem}5`;
    const transformedItem = `${analyticItem}3`;

    let positiveFound = false;
    let negativeFound = false;

    for (let i = filterSet[filterGroup].length - 1; i > -1; i--) {
      if (filterSet[filterGroup][i].analyticItem === originalItem) {
        const newFilters = [];
        switch (filterSet[filterGroup][i].value) {
          case 'very positive':
          case 'somewhat positive':
            if (!positiveFound) {
              newFilters.push({
                ...filterSet[filterGroup][i],
                analyticItem: transformedItem,
                displayValue: 'positive',
                displayName: 'positive',
                value: 'positive',
              });
              positiveFound = true;
            }
            break;
          case 'very negative':
          case 'somewhat negative':
            if (!negativeFound) {
              newFilters.push({
                ...filterSet[filterGroup][i],
                analyticItem: transformedItem,
                displayValue: 'negative',
                displayName: 'negative',
                value: 'negative',
              });
              negativeFound = true;
            }
            break;
          default:
            newFilters.push({ ...filterSet[filterGroup][i], analyticItem: transformedItem });
        }
        filterSet[filterGroup].splice(i, 1, ...newFilters);
      }
    }
    if (!fromDocument && filterSet[filterGroup].length === 3) {
      filterSet[filterGroup] = [];
    }
  }

  public static transformDocumentPolarityFilter3(filterSet: FilterSet, analyticItem: string): void {
    FilterService.transformAnalyticFeaturePolarityFilter3(filterSet, analyticItem, 'and', true);
    FilterService.transformAnalyticFeaturePolarityFilter3(filterSet, analyticItem, 'or', true);
    FilterService.transformAnalyticFeaturePolarityFilter3(filterSet, analyticItem, 'not', true);
  }

  public static setAnalayticFeaturePolarityFilter(
    filterSet: FilterSet,
    analyticItem: string,
    value: string,
    toAdd: boolean
  ): void {
    if (!filterSet) {
      return;
    }
    const idx = filterSet.or.findIndex(
      (filter) => filter.analyticItem === analyticItem && filter.value === value
    );
    const found = idx !== -1;
    if (!toAdd && found) {
      // remove from or
      filterSet.or.splice(idx, 1);
    } else if (toAdd && !found) {
      // add to the or
      filterSet.or.push({
        analyticItem,
        hidden: true,
        value,
      });
    }
  }

  public static setAnalyticItemFilter(
    filterSet: FilterSet,
    analyticItem: string,
    subType: string,
    value: string,
    displayName?: string,
    taxonomyPath?: string,
    selectedSections?: SelectedSection[]
  ): void {
    const idx = filterSet.and.findIndex(
      (filter) => filter.analyticItem === analyticItem && filter.value === value
    );

    const found = idx !== -1;
    if (!found) {
      if (!selectedSections || !selectedSections.length) {
        const filter = {
          analyticItem,
          value,
          displayName,
          taxonomyPath,
        } as any;

        if (subType) {
          filter.subLabel = subType;
        }
        filterSet.and.push(filter);
      } else {
        selectedSections.forEach((section) => {
          const filter = {
            analyticItem,
            value,
            displayName,
            taxonomyPath,
            sectionField: section.field,
            sectionBuckets: section.buckets,
            selectedSections: [
              {
                field: section.field,
                buckets: section.buckets,
              },
            ],
          } as any;
          filterSet.and.push(filter);
        });
      }
    }
  }

  public static clearFeatureSentimentFilters(filterSet: FilterSet, analyticItem: string): void {
    if (!filterSet || !filterSet.or) {
      return;
    }
    filterSet.or = filterSet.or.filter((filter) => filter.analyticItem !== analyticItem);
  }

  public static analyticItemShouldBeVisible(
    filterSet: FilterSet,
    analyticItem: string,
    value: string
  ): boolean {
    if (!filterSet) {
      return true;
    }
    const found = filterSet.or.findIndex(
      (filter) => filter.analyticItem === analyticItem && filter.value === value
    );
    return found === -1;
  }

  public static featurePolarityShouldBeVisible(
    filterSet: FilterSet,
    analyticItem: string,
    value: string
  ): boolean {
    if (!filterSet) {
      return true;
    }
    const featureFilters = filterSet.or.filter((filter) => filter.analyticItem === analyticItem);
    if (featureFilters.length === 0) {
      return true;
    }
    const found = filterSet.or.findIndex(
      (filter) => filter.analyticItem === analyticItem && filter.value === value
    );
    return found !== -1;
  }

  public static featurePolarityShouldBeVisibleOnChart(
    filterSet: FilterSet,
    analyticItem: string,
    value: string
  ): boolean {
    if (!filterSet) {
      return true;
    }
    const featureFiltersAnd = filterSet.and.filter(
      (filter) => filter.analyticItem === analyticItem
    );
    const featureFiltersOr = filterSet.or.filter((filter) => filter.analyticItem === analyticItem);
    const featureFiltersNot = filterSet.not.filter(
      (filter) => filter.analyticItem === analyticItem
    );
    if (
      featureFiltersAnd.length === 0 &&
      featureFiltersOr.length === 0 &&
      featureFiltersNot.length === 0
    ) {
      return true;
    }
    const foundAnd =
      filterSet.and.findIndex(
        (filter) => filter.analyticItem === analyticItem && filter.value === value
      ) !== -1;
    const foundOr =
      filterSet.or.findIndex(
        (filter) => filter.analyticItem === analyticItem && filter.value === value
      ) !== -1;
    const foundNot =
      filterSet.not.findIndex(
        (filter) => filter.analyticItem === analyticItem && filter.value === value
      ) !== -1;
    return (
      (featureFiltersAnd.length === 0 || foundAnd) &&
      (featureFiltersOr.length === 0 || foundOr) &&
      !foundNot
    );
  }

  public static isSentimentPolarityFilter(filter: Filter): boolean {
    return filter.analyticItem.includes('sentimentPolarity');
  }

  public static filterSetHasFilters(filterSet: FilterSet): boolean {
    if (!filterSet) {
      return false;
    }
    if (filterSet.and && filterSet.and.find((f) => !f.hidden)) {
      return true;
    }
    if (filterSet.or && filterSet.or.find((f) => !f.hidden)) {
      return true;
    }
    if (filterSet.and && filterSet.not.find((f) => !f.hidden)) {
      return true;
    }
    return false;
  }

  public static analyticItemFilterSetsHasFilters(
    filters: AnalyticItemFilterSets,
    textFilters: UserSearchFilter
  ): boolean {
    const f = { ...filters, ...textFilters };
    for (const key in f) {
      if (FilterService.filterSetHasFilters(f[key])) {
        return true;
      }
    }
    return false;
  }

  public static hasSentiemntFilterPolarityFilters(widget: Widget): boolean {
    const polarities = FilterService.createSentimentFilterPolarities(widget);
    for (const polarity of polarities) {
      if (!polarity.checked) {
        return true;
      }
    }
    return false;
  }

  public static createSentimentFilterPolarities(widget: Widget): SentimentFilterPolarity[] {
    const scale = widget.payload.sentimentScale;
    const analyticItem = `analyses.${widget.payload.analyticItem}.sentimentPolarity${widget.payload.sentimentScale}`;
    const filterSet = widget.payload.filters[widget.payload.analyticItem] as FilterSet;
    let polarities: SentimentFilterPolarity[] = [];
    switch (scale) {
      case 3:
        polarities = [
          {
            value: 'negative',
            checked: FilterService.featurePolarityShouldBeVisible(
              filterSet,
              analyticItem,
              'negative'
            ),
          },
          {
            value: 'neutral',
            checked: FilterService.featurePolarityShouldBeVisible(
              filterSet,
              analyticItem,
              'neutral'
            ),
          },
          {
            value: 'positive',
            checked: FilterService.featurePolarityShouldBeVisible(
              filterSet,
              analyticItem,
              'positive'
            ),
          },
        ];
        break;
      case 5:
        polarities = [
          {
            value: 'very negative',
            checked: FilterService.featurePolarityShouldBeVisible(
              filterSet,
              analyticItem,
              'very negative'
            ),
          },
          {
            value: 'somewhat negative',
            checked: FilterService.featurePolarityShouldBeVisible(
              filterSet,
              analyticItem,
              'somewhat negative'
            ),
          },
          {
            value: 'neutral',
            checked: FilterService.featurePolarityShouldBeVisible(
              filterSet,
              analyticItem,
              'neutral'
            ),
          },
          {
            value: 'somewhat positive',
            checked: FilterService.featurePolarityShouldBeVisible(
              filterSet,
              analyticItem,
              'somewhat positive'
            ),
          },
          {
            value: 'very positive',
            checked: FilterService.featurePolarityShouldBeVisible(
              filterSet,
              analyticItem,
              'very positive'
            ),
          },
        ];
        break;
      default:
        console.error(`Unknown sentiment scale ${scale}`);
    }
    return polarities;
  }

  public static createSentimentChartFilterEvents(widget: Widget): SentimentFilterEventsManager {
    const isMetadata = widget.payload.standardFieldSelected || widget.payload.customFieldSelected;
    let filterAnalyticItem = isMetadata
      ? 'analyses.sentimentPolarity'
      : `analyses.${widget.payload.analyticItem}.sentimentPolarity`;
    filterAnalyticItem = filterAnalyticItem + widget.payload.sentimentScale;
    let filterItem = widget.payload.analyticItem;
    if (widget.payload.standardFieldSelected || widget.payload.customFieldSelected) {
      filterItem = 'analyses';
    }
    const filterSet = widget.payload.filters[filterItem] as FilterSet;
    let sentimentValues = ['positive', 'neutral', 'negative'];
    if (widget.payload.sentimentScale === 5) {
      sentimentValues = [
        'very positive',
        'somewhat positive',
        'neutral',
        'very negative',
        'somewhat negative',
      ];
    }
    let events = {
      legendItemClick(_$event) {},
      hide: (_$event) => {},
      show: (_$event) => {},
    };
    const shouldBeVisible = (value: string, isTotalValue = false): boolean => {
      if (isTotalValue) {
        return widget?.payload?.legends?.[value] ?? true;
      }
      return FilterService.featurePolarityShouldBeVisibleOnChart(
        filterSet,
        filterAnalyticItem,
        value
      );
    };

    if (filterSet) {
      const polarityFilters = filterSet.or.filter((filter) =>
        FilterService.isSentimentPolarityFilter(filter)
      );
      events = {
        legendItemClick($event) {
          const value: string =
            $event.target.name === 'Total volume' ? 'total' : $event.target.name.toLowerCase();
          if (value === 'total') {
            return false;
          }

          const andFilter = filterSet.and.filter(
            (filter) => filter.analyticItem === filterAnalyticItem
          );

          if (andFilter.length > 0) {
            const foundAnd = andFilter.findIndex((filter) => filter.value === value);
            if (foundAnd === -1) {
              return false;
            }
          }
          const foundNot = filterSet.not.findIndex(
            (filter) => filter.analyticItem === filterAnalyticItem && filter.value === value
          );
          return foundNot === -1;
        },
        hide: ($event) => {
          const value: string =
            $event.target.name === 'Total volume' ? 'total' : $event.target.name.toLowerCase();
          if (value === 'total') {
            widget.payload.legends[$event.target.name] = false;
            return;
          }
          //If there are no sentiment polarity filters yet, then we need to add all the others that weren't selected
          if (polarityFilters.length < 1) {
            sentimentValues
              .filter((sentiment) => {
                const andFilter = filterSet.and.filter(
                  (filter) => filter.analyticItem === filterAnalyticItem
                );

                return (
                  andFilter.length === 0 ||
                  andFilter.findIndex(
                    (filter) =>
                      filter.analyticItem === filterAnalyticItem && filter.value === sentiment
                  ) !== -1
                );
              })
              .filter(
                (sentiment) =>
                  filterSet.not.findIndex(
                    (filter) =>
                      filter.analyticItem === filterAnalyticItem && filter.value === sentiment
                  ) === -1
              )
              .forEach((sentiment) => {
                if (sentiment !== value) {
                  filterSet.or.push({
                    analyticItem: filterAnalyticItem,
                    value: sentiment,
                    hidden: !isMetadata,
                  });
                }
              });
            widget.payload.refresh(widget.id);
            return;
          }

          const found = filterSet.or.findIndex(
            (filter) => filter.analyticItem === filterAnalyticItem && filter.value === value
          );
          if (found !== -1) {
            filterSet.or.splice(found, 1);
            widget.payload.refresh(widget.id);
          }
        },
        show: ($event) => {
          const value: string =
            $event.target.name === 'Total volume' ? 'total' : $event.target.name.toLowerCase();
          if (value === 'total') {
            widget.payload.legends[$event.target.name] = true;
            $event.preventDefault();
            return;
          }
          if (!widget.payload.refresh) {
            console.error('no refresh on', widget);
            return;
          }

          //If this addition would bring us to a full set of filters then clear them out
          //It's equivalent and makes nicer queries.
          if (polarityFilters.length === widget.payload.sentimentScale - 1) {
            this.clearFeatureSentimentFilters(filterSet, filterAnalyticItem);
            widget.payload.refresh(widget.id);
            return;
          }
          const found = filterSet.or.findIndex(
            (filter) => filter.analyticItem === filterAnalyticItem && filter.value === value
          );
          if (found === -1) {
            filterSet.or.push({
              analyticItem: filterAnalyticItem,
              value,
              hidden: !isMetadata,
            });
            widget.payload.refresh(widget.id);
          }
        },
      };
      return { events, shouldBeVisible };
    }
    return { events, shouldBeVisible };
  }

  public buildFilterStringClearPolarityFilters(
    filterSet: AnalyticItemFilterSets,
    analyticItem: string,
    forQueryString = false
  ): string {
    if (filterSet) {
      //Remove all or polarity feature filters that are not in the current selected analytic item
      if (filterSet['themes']) {
        filterSet['themes'].or =
          analyticItem !== 'themes'
            ? filterSet['themes'].or.filter(
                (filter) => !FilterService.isSentimentPolarityFilter(filter)
              )
            : filterSet['themes'].or;
      }
      if (filterSet['phrases']) {
        filterSet['phrases'].or =
          analyticItem !== 'phrases'
            ? filterSet['phrases'].or.filter(
                (filter) => !FilterService.isSentimentPolarityFilter(filter)
              )
            : filterSet['phrases'].or;
      }
      if (filterSet['topics']) {
        filterSet['topics'].or =
          analyticItem !== 'topics'
            ? filterSet['topics'].or.filter(
                (filter) => !FilterService.isSentimentPolarityFilter(filter)
              )
            : filterSet['topics'].or;
      }
      if (filterSet['entities']) {
        filterSet['entities'].or =
          analyticItem !== 'entities'
            ? filterSet['entities'].or.filter(
                (filter) => !FilterService.isSentimentPolarityFilter(filter)
              )
            : filterSet['entities'].or;
      }
    }
    return this.buildFilterString(filterSet, forQueryString);
  }

  public numericRangeFilterToModel(filterValue) {
    const modelValue: NumericFilterDisplayValue = {
      leftOperand: {
        operator: undefined,
        value: undefined,
      },
      rightOperand: {
        operator: undefined,
        value: undefined,
      },
    };

    const extractOperand = (part) => {
      const opPart = part.split(' ');
      return {
        operator: opPart[0],
        value: opPart[1],
      };
    };

    let parts = [filterValue.replace('numericrange/', '')];
    if (filterValue.includes('OR')) {
      parts = parts[0].split('OR').map((v) => v.trim());
      modelValue.booleanOperator = 'OR';
    } else if (filterValue.includes('AND')) {
      parts = parts[0].split('AND').map((v) => v.trim());
      modelValue.booleanOperator = 'AND';
    }

    modelValue.leftOperand = extractOperand(parts[0]);
    if (parts[1]) {
      modelValue.rightOperand = extractOperand(parts[1]);
    }

    return modelValue;
  }

  public numericRangeModelToFilter(modelValue: NumericFilterDisplayValue) {
    let filterValue = `numericrange/${modelValue.leftOperand.operator} ${modelValue.leftOperand.value}`;

    if (modelValue.booleanOperator) {
      filterValue = `${filterValue} ${modelValue.booleanOperator} ${modelValue.rightOperand.operator} ${modelValue.rightOperand.value}`;
    }

    return filterValue;
  }

  public translateTitle(analyticItem: string): string {
    const item = this.getFilterConfigItem(analyticItem);
    if (!item) {
      return analyticItem;
    }
    return item.title;
  }

  public getShortLabel(analyticItem: string): string {
    if (!analyticItem) {
      return undefined;
    }

    if (analyticItem.startsWith('metadata.')) {
      return `MD`;
    }

    const item = this.getFilterConfigItem(analyticItem);
    if (!item) {
      return analyticItem;
    }
    return item.shortLabel;
  }

  public getPillTexts(filter: Filter, maxLength?: number): FilterPillTexts {
    let text: string;
    let textExtra: string;

    if (filter.analyticItem.includes('taxonomy')) {
      const taxonomyPath = filter.value.split('|');
      taxonomyPath.splice(-1, 1);

      text = filter.displayValue;
      textExtra = taxonomyPath.join(' / ');
    } else if (
      (filter.analyticItem.includes('metadata') ||
        isAnalyticItemTextualSize(filter.analyticItem)) &&
      filter.displayTitle
    ) {
      if (maxLength && filter.displayTitle.length + filter.displayValue.length > maxLength) {
        text = `${filter.displayTitle} /`;
        textExtra = filter.displayValue;
      } else {
        text = `${filter.displayTitle} / ${filter.displayValue}`;
      }
    } else if (filter.analyticItem === 'createdAt') {
      text = this.computeLabelForDateFilter(filter.value);
    } else if (filter.subLabel) {
      if (maxLength && filter.displayValue.length + filter.subLabel.length > maxLength) {
        text = `${filter.subLabel} /`;
        textExtra = filter.displayValue;
      } else {
        text = `${filter.subLabel} / ${filter.displayValue}`;
      }
    } else {
      text = filter.displayValue;
    }

    const shortLabel = filter.analyticItemShortLabel || this.getShortLabel(filter.analyticItem);

    return {
      text: Utils.unescapeHtml(text),
      textExtra: Utils.unescapeHtml(textExtra),
      shortLabel,
    };
  }

  public translateKey(analyticItem: string, key: string): string {
    const localKey = key.replace('_exists_', 'Exists').replace('_notExists_', 'Does Not Exist');

    if (localKey.startsWith('numericrange/')) {
      return localKey.replace('numericrange/', '');
    }

    const config = this.getFilterConfigItem(analyticItem);
    if (!config || !config.computeLabel) {
      return localKey;
    }

    return config.computeLabel(localKey);
  }

  private buildUserSearchString(widgetQueryOptions: WidgetQueryOptions, isGlobal: boolean = false) {
    if (widgetQueryOptions?.userSearch?.userSearch) {
      const userSearchFilter = widgetQueryOptions.userSearch;
      const withSectionBuckets = {} as any;
      const withoutSectionBuckets = {} as any;

      let hasAnyWithSectionBuckets = false;
      let hasAnyWithoutSectionBuckets = false;

      const initFunction = (obj: any, key: any, group: any): void => {
        obj[key] ??= {} as any;
        obj[key][group] ??= [];
      };

      for (const key of Object.keys(userSearchFilter)) {
        for (const group of Object.keys(userSearchFilter[key])) {
          userSearchFilter[key][group].forEach((item) => {
            if (
              (Array.isArray(item.sectionBuckets) && item.sectionBuckets.length > 0) ||
              item.selectedSections?.length
            ) {
              initFunction(withSectionBuckets, key, group);
              withSectionBuckets[key][group].push(item);
              hasAnyWithSectionBuckets = true;
            } else {
              initFunction(withoutSectionBuckets, key, group);
              withoutSectionBuckets[key][group].push(item);
              hasAnyWithoutSectionBuckets = true;
            }
          });
        }
      }

      if (isGlobal && hasAnyWithoutSectionBuckets) {
        return this.buildString(withoutSectionBuckets, true, widgetQueryOptions, false);
      }

      if (!isGlobal && hasAnyWithSectionBuckets) {
        return this.buildString(withSectionBuckets, true, widgetQueryOptions, false);
      }
    }

    return undefined;
  }

  public buildQueryString(
    filterSet: AnalyticItemFilterSets,
    widgetQueryOptions?: WidgetQueryOptions
  ): string {
    const targetFilterSet = FilterService.getDefaultFilterSet();
    const comparisonSectionFilters: SelectedSection[] = [];
    let hasSectionFilterForAnyAnalyticalItem = false;
    if (filterSet) {
      for (const key of Object.keys(filterSet)) {
        for (const operator of ['and', 'or', 'not']) {
          if (filterSet[key][operator]) {
            for (const filter of filterSet[key][operator]) {
              const config = this.getFilterConfigItem(filter.analyticItem);
              if (filter.sectionField || filter.selectedSections?.length) {
                hasSectionFilterForAnyAnalyticalItem = true;
              }

              // DASH-9694: Comparison Section Filters for analitic items are handled through the widgetQueryOptions selectedSection param.
              // Result of it should be the following for phrases: (analyses.phrases.sectionInfoList.speakerName:\"John Doe\" IN analyses.phrases.sectionInfoList),
              // but when section property is selected as analytic item and the comparison filter is a section level metadata filter,
              // then we must append a prefix at the begining of the filter name.
              const comparisonSectionFilter = FilterUtils.isComparisonSectionFilter(filter);
              if (comparisonSectionFilter) {
                const negated = operator === 'not';
                const prefix =
                  FilterUtils.isSectionMetadataComparisonFilter(filter) &&
                  widgetQueryOptions?.sectionPropertySelected
                    ? 'metadata.'
                    : '';
                comparisonSectionFilters.push(
                  FilterUtils.comparisonFilterToSelectedSection(filter, prefix, negated)
                );
              }

              // DASH-9694: Comparison Section Filters are handled through differently (see comment above)
              if ((!config || !config.nested) && !comparisonSectionFilter) {
                targetFilterSet[key][operator].push(filter);
              }
            }
          }
        }
      }
    }

    if (widgetQueryOptions?.userSearch) {
      for (const key of Object.keys(widgetQueryOptions.userSearch)) {
        for (const group of Object.keys(widgetQueryOptions.userSearch[key])) {
          widgetQueryOptions.userSearch[key][group].forEach((item) => {
            if (
              (Array.isArray(item.sectionBuckets) && item.sectionBuckets.length > 0) ||
              item.selectedSections?.length
            ) {
              hasSectionFilterForAnyAnalyticalItem = true;
            }
          });
        }
      }
    }

    // DASH-9694: For comparison section filters, we need to add the comparison sections to the widgetQueryOptions in order to build the appropriate query.
    const updatedWidgetQueryOptions = FilterUtils.getWidgetQueryOptionsWithSelectedSectionsApplied(
      widgetQueryOptions,
      comparisonSectionFilters
    );

    // Should add as global section filter
    const addGlobalSectionFilter =
      !hasSectionFilterForAnyAnalyticalItem &&
      updatedWidgetQueryOptions?.sectionFilter !== null &&
      updatedWidgetQueryOptions?.sectionFilter !== undefined;

    const query = this.buildString(
      targetFilterSet,
      true,
      updatedWidgetQueryOptions,
      addGlobalSectionFilter
    );
    return this.addSectionedUserSearchToQuery(query, updatedWidgetQueryOptions);
  }

  //This is building the filter string used by reports.  Since reports handle the analysis level and nesting themselves
  //We don't need the extra actions that the buildQueryString does.
  public buildFilterString(
    filterSet: AnalyticItemFilterSets,
    forQueryString = false,
    forceApplyComparisonFilter = false
  ): string {
    const targetFilterSet = FilterService.getDefaultFilterSet();

    if (filterSet) {
      for (const key of Object.keys(filterSet)) {
        for (const operator of ['and', 'or', 'not']) {
          if (filterSet[key][operator]) {
            for (const filter of filterSet[key][operator]) {
              const config = this.getFilterConfigItem(filter.analyticItem);
              const comparisonSectionFilter = FilterUtils.isComparisonSectionFilter(filter);

              // DASH-9694: Comparison Section Filters are handled through the widgetQueryOptions selectedSection param
              if (
                (config && config.nested && !comparisonSectionFilter) ||
                (comparisonSectionFilter && forceApplyComparisonFilter)
              ) {
                targetFilterSet[key][operator].push(filter);
              }
            }
          }
        }
      }
    }
    return this.buildString(targetFilterSet, forQueryString);
  }

  public addSectionedUserSearchToQuery(query: string, widgetQueryOptions?: WidgetQueryOptions) {
    const userSearchSectionString = this.buildUserSearchString(widgetQueryOptions, false);
    if (!userSearchSectionString || !widgetQueryOptions?.sectionFilter) {
      return query;
    }

    let userSearchSectionQuery = this.getGlobalSectionListQueryPart(
      this.getSelectedSectionsFromWidgetQueryOptions(widgetQueryOptions),
      userSearchSectionString
    );

    if (query) {
      userSearchSectionQuery = `${userSearchSectionQuery} AND ${query}`;
    }

    return userSearchSectionQuery;
  }

  public calculateQueryClause(
    filter: Filter,
    operatorType: 'and' | 'or' | 'not',
    forQueryString = false,
    widgetQueryOptions?: WidgetQueryOptions
  ): string {
    const addSectionToQuery = (
      query: string,
      analyticItem: string,
      selectedSections: SelectedSection[],
      config: FilterConfig
    ): string => {
      return `${query} AND ${this.getAnalyticItemSectionInfoListQueryPart(
        analyticItem,
        selectedSections,
        config
      )}`;
    };

    const hasFilterSectionField =
      (filter.sectionField && filter.sectionField !== Constants.ALL_SECTION_ID) ||
      filter.selectedSections;
    const hasFilterInSections = FilterUtils.hasInSectionsFilter(widgetQueryOptions);

    const config = this.getFilterConfigItem(filter.analyticItem);
    const escapedValue = FilterUtils.computeValue(filter.analyticItem, filter.value, config);

    const isSectionMetadataComparisonFilter = FilterUtils.isSectionMetadataComparisonFilter(filter);
    // Handle section properties as nested analyses objects
    if (
      isSectionMetadataComparisonFilter ||
      filter.analyticItem.startsWith('sectionProperties.') ||
      filter.analyticItem.startsWith('analyses.sectionProperties.') ||
      widgetQueryOptions?.sectionPropertySelected
    ) {
      // Specify property path: for section level metadata fields it will be "metadata."
      const propertyPath = isSectionMetadataComparisonFilter ? 'metadata.' : '';
      const propertyName = filter.analyticItem
        .replace('sectionProperties.', '')
        .replace('analyses.sectionProperties.', '')
        .replace('analyses.', '');
      let query = `analyses.flatSections.${propertyPath}${propertyName}:${escapedValue}`;

      if (hasFilterSectionField) {
        query = addSectionToQuery(
          query,
          'flatSections',
          this.getSelectedSectionsFromFilter(filter),
          config
        );
      }

      if (widgetQueryOptions?.selectedSection?.length) {
        query = addSectionToQuery(
          query,
          'flatSections',
          widgetQueryOptions.selectedSection,
          config
        );
      }

      return `(${query} IN analyses.flatSections)`;
    }

    if (
      filter.analyticItem.includes('taxonomy') &&
      !FilterService.isSentimentPolarityFilter(filter)
    ) {
      // DASH-10254: We show direct hits for a node when looking in sections
      // and continue to show hits on child nodes as well as direct hits
      // when looking at the whole document.
      // [UPDATE] DASH-10430: The default behavior should always be to use regex,
      // and direct hits should only be used when clicking on a point (filter.kind === 'pointFilter').
      const filterValueFunction = (string: string) => {
        return (hasFilterSectionField || hasFilterInSections) && filter.kind === 'pointFilter'
          ? `"${escapeQuotes(string)}"`
          : `/${FilterUtils.escapeTaxonomyStartingWith(string)}/`;
      };
      let query = `analyses.taxonomy.taxonomyFullPath:${filterValueFunction(filter.value)}`;

      if (hasFilterSectionField) {
        query = addSectionToQuery(
          query,
          'taxonomy',
          this.getSelectedSectionsFromFilter(filter),
          config
        );
      }

      if (hasFilterInSections) {
        query = addSectionToQuery(query, 'taxonomy', widgetQueryOptions.selectedSection, config);
      }

      return `(${query} IN analyses.taxonomy)`;
    }

    if (!escapedValue) {
      return '';
    }

    //If the field has a sublabel it contains two fields that must be AND'd together.
    if (
      Utils.unescapeBlank(escapedValue) === Constants.BLANK &&
      filter.analyticItem !== 'userSearch'
    ) {
      return `NOT _exists_:${filter.analyticItem}`;
    } else if (config && config.fieldName && filter.subLabel) {
      let rollupField;
      if (filter.analyticItem.includes('entities')) {
        rollupField = 'entityType';
      } else if (filter.analyticItem.includes('topics')) {
        rollupField = 'type';
        if (filter.subLabel && FilterService.clauseModelTypes.includes(filter.subLabel)) {
          rollupField = 'purpose';
        }
      } else if (filter.analyticItem.includes('eie')) {
        rollupField = 'purpose';
      } else {
        rollupField = 'label';
      }

      const subTypes = filter.subLabel.split(',');
      const subTypeQuery = [];
      subTypes.forEach((etf) => {
        let stq = `${config.fieldName}.${rollupField}:"${escapeQuotes(etf)}" AND ${
          config.fieldName
        }.title:${escapedValue}`;

        if (widgetQueryOptions?.selectedSection?.length) {
          stq = addSectionToQuery(
            stq,
            widgetQueryOptions.analyticItem,
            widgetQueryOptions.selectedSection,
            config
          );
        } else {
          stq = `(${stq})`;
        }

        subTypeQuery.push(stq);
      });

      let queryClause = `${subTypeQuery.join(' OR ')}`;

      if (hasFilterSectionField) {
        queryClause = `${queryClause} AND ${this.getSectionInfoListQueryPart(
          config.fieldName,
          this.getSelectedSectionsFromFilter(filter),
          config
        )}`;
      }

      if (
        operatorType !== 'or' ||
        filter.analyticItem.includes('entities') ||
        filter.analyticItem.includes('topics') ||
        filter.analyticItem.includes('eie')
      ) {
        queryClause = `(${queryClause} IN ${config.fieldName})`;
      }
      return `${queryClause}`;
    } else if (FilterService.isSentimentPolarityFilter(filter) && hasFilterSectionField) {
      return this.getFlatSectionsSentimentQueryPart(
        this.getSelectedSectionsFromWidgetQueryOptions(widgetQueryOptions),
        config,
        escapedValue,
        widgetQueryOptions.sentimentScale
      );
    } else if (
      config &&
      config.fieldName &&
      (!FilterService.isSentimentPolarityFilter(filter) || forQueryString)
    ) {
      let analyticItem = JSON.parse(JSON.stringify(filter.analyticItem));
      if (analyticItem.startsWith('ML-')) {
        analyticItem = analyticItem.slice(3);
      }
      let queryClause = `${analyticItem}:${escapedValue}`;

      if (widgetQueryOptions?.selectedSection?.length) {
        queryClause = addSectionToQuery(
          queryClause,
          widgetQueryOptions.analyticItem,
          widgetQueryOptions.selectedSection,
          config
        );
      }

      if (hasFilterSectionField) {
        queryClause = `${queryClause} AND ${this.getSectionInfoListQueryPart(
          config.fieldName,
          this.getSelectedSectionsFromFilter(filter),
          config
        )}`;
      }

      if (operatorType !== 'or') {
        queryClause = `(${queryClause} IN ${config.fieldName})`;
      }

      return queryClause;
    } else if (filter.value.includes('numericrange/')) {
      return `${filter.value.includes('!=') ? 'NOT' : ''} (${filter.analyticItem}:${escapedValue})`;
    } else if (filter.value === '_exists_') {
      return `_exists_:${filter.analyticItem}`;
    } else if (filter.value === '_notExists_') {
      return `NOT _exists_:${filter.analyticItem}`;
    } else if (filter.analyticItem === 'userSearch') {
      if (hasFilterSectionField) {
        return `analyses.flatSections.value:${escapedValue}`;
      }
      return `(${escapedValue})`;
    } else {
      return `${filter.analyticItem}:${escapedValue}`;
    }
  }

  public buildString(
    filterSet: AnalyticItemFilterSets,
    forQueryString = false,
    widgetQueryOptions?: WidgetQueryOptions,
    addGlobalSectionFilter: boolean = false
  ): string {
    if (!filterSet) {
      return '';
    }
    const distinctQueries = [];
    const fullOrParts = [];
    for (const key of Object.keys(filterSet)) {
      const parts = [];

      const andGroup = [];

      if (filterSet[key].and) {
        for (const filter of filterSet[key].and) {
          const clause = this.calculateQueryClause(
            filter,
            'and',
            forQueryString,
            widgetQueryOptions
          );
          if (clause) {
            andGroup.push(clause);
          }
        }
      }

      if (andGroup.length > 0) {
        const groupStr = andGroup.join(` AND `);
        parts.push(groupStr);
      }

      const orGroup = [];

      if (filterSet[key].or) {
        for (const filter of filterSet[key].or) {
          const clause = this.calculateQueryClause(
            filter,
            'or',
            forQueryString,
            widgetQueryOptions
          );
          if (clause) {
            orGroup.push(clause);
          }
        }
      }

      if (orGroup.length > 0) {
        const groupStr = orGroup.join(` OR `);
        if (forQueryString && itemFilterSetsInValue.hasOwnProperty(key)) {
          fullOrParts.push(`(${groupStr} IN ${itemFilterSetsInValue[key]})`);
        } else {
          fullOrParts.push(`(${groupStr})`);
        }
      }

      const notGroup = [];
      if (filterSet[key].not) {
        for (const filter of filterSet[key].not) {
          const clause = this.calculateQueryClause(
            filter,
            'not',
            forQueryString,
            widgetQueryOptions
          );
          if (clause.includes(' IN ')) {
            notGroup.push(`NOT ${clause}`);
          } else if (clause.startsWith('NOT ')) {
            notGroup.push(`(${clause.slice(4)})`);
          } else if (clause) {
            notGroup.push(`(NOT ${clause})`);
          }
        }
      }

      if (notGroup.length > 0) {
        const groupStr = notGroup.join(' AND ');
        parts.push(groupStr);
      }

      if (parts.length > 0) {
        const queryStr = parts.join(' AND ');
        distinctQueries.push(queryStr);
      }
    }

    //Put all of the ORs together
    if (fullOrParts.length > 0) {
      if (fullOrParts.length > 1) {
        distinctQueries.push(`(${fullOrParts.join(' OR ')})`);
      } else {
        distinctQueries.push(fullOrParts[0]);
      }
    }

    if (addGlobalSectionFilter) {
      const secQuery = this.getGlobalSectionListQueryPart(
        this.getSelectedSectionsFromWidgetQueryOptions(widgetQueryOptions)
      );
      distinctQueries.push(secQuery);
    }

    if (distinctQueries.length > 0) {
      const queryStr = distinctQueries.join(' AND ');
      return queryStr;
    }
    return '';
  }

  public getFilterConfigItem(item: string) {
    if (item.includes('sentimentPolarity') && (item.endsWith('5') || item.endsWith('3'))) {
      item = item.substring(0, item.length - 1);
    }
    return this.filterConfig[item];
  }

  public getFilterSetNameByAnalyticItem(filter: Filter): string {
    let filterSet = '';

    const filterConfig = this.getFilterConfigItem(filter.analyticItem);
    if (filterConfig) {
      filterSet = filterConfig.filterSet;
    }

    if (filter.analyticItem.includes('metadata')) {
      filterSet = 'metadata';
    } else if (filter.analyticItem.includes('tags')) {
      filterSet = 'tags';
    } else if (filter.analyticItem === 'analyses.autoCategories.title') {
      // for legacy dashboards
      filterSet = 'autoCategories';
    }

    return filterSet;
  }

  public isAllowedForSection(filter: Filter): boolean {
    if (FilterService.isSentimentPolarityFilter(filter)) {
      return true;
    }

    const filterSetName = this.getFilterSetNameByAnalyticItem(filter);
    return Constants.SECTION_ALLOWED_ANALYTIC_ITEM_TYPES.includes(filterSetName);
  }

  //Making a copy so showDocumentsForItem doesn't keep updating the filters
  private createCopy(filterSet: FilterSet): FilterSet {
    if (!filterSet) {
      return { and: [], or: [], not: [] };
    }
    return JSON.parse(JSON.stringify(filterSet));
  }

  public getQueryForFilteredDocuments(
    widgetQueryOptions: WidgetQueryOptions,
    forExternalDataOrs = false,
    forReportFilter = false,
    skipFeatureSentiment = false
  ) {
    let query = this.getQueryForDocuments(
      widgetQueryOptions,
      forExternalDataOrs,
      forReportFilter,
      skipFeatureSentiment
    );

    // If this is a Selected Items widget AND them on here
    const selectedItems: string[] = widgetQueryOptions.selectedItems;
    if (!selectedItems || selectedItems.length === 0) {
      return query;
    }

    const analyticItem: string = widgetQueryOptions.analyticItem;
    let selectedItemsQuery = '';
    if (['topics', 'entities', 'themes', 'phrases', 'taxonomy'].includes(analyticItem)) {
      let titleField = 'title';
      if (analyticItem === 'themes') {
        titleField = 'normalizedTitle';
      } else if (analyticItem === 'taxonomy') {
        titleField = 'id';
      }
      const parts: string[] = [];
      selectedItems.forEach((selectedItem) => {
        let part = `analyses.${analyticItem}.${titleField}:"${escapeQuotes(selectedItem)}"`;
        if (widgetQueryOptions.subType) {
          const subTypes = widgetQueryOptions.subType.split(',');
          let typeAttr = analyticItem === 'entities' ? 'entityType' : 'type';

          const entityTypeQuery = [];

          subTypes.forEach((etf) => {
            typeAttr = analyticItem === 'entities' ? 'entityType' : 'type';
            if (FilterService.clauseModelTypes.includes(etf) && analyticItem === 'topics') {
              typeAttr = 'purpose';
            }
            entityTypeQuery.push(
              `(analyses.${analyticItem}.${typeAttr}:"${etf}" AND analyses.${analyticItem}.${titleField}:"${escapeQuotes(
                selectedItem
              )}")`
            );
          });

          part = `${entityTypeQuery.join(' OR ')}`;
        }
        parts.push(part);
      });
      selectedItemsQuery = `(${parts.join(' OR ')} IN analyses.${analyticItem})`;
    } else if (analyticItem?.startsWith('sectionProperties.')) {
      // Handle section properties - they use flatSections
      const propertyName = analyticItem.substring('sectionProperties.'.length);
      const parts: string[] = [];
      selectedItems.forEach((selectedItem) => {
        const part = `analyses.flatSections.${propertyName}:"${escapeQuotes(selectedItem)}"`;
        parts.push(part);
      });
      selectedItemsQuery = `(${parts.join(' OR ')} IN analyses.flatSections)`;
    } else {
      const fieldPrefix = FilterService.standardFields.includes(analyticItem) ? '' : 'metadata.';
      const parts: string[] = [];
      selectedItems.forEach((selectedItem) => {
        const part = `${fieldPrefix}${analyticItem}:"${escapeQuotes(selectedItem)}"`;
        parts.push(part);
      });
      selectedItemsQuery = `(${parts.join(' OR ')})`;
    }

    if (query) {
      query = `${query} AND ${selectedItemsQuery}`;
    } else {
      query = selectedItemsQuery;
    }
    return query;
  }

  //split out for easier testing
  public getQueryForDocuments(
    widgetQueryOptions: WidgetQueryOptions,
    forExternalDataOrs = false,
    forReportFilter = false,
    skipFeatureSentiment = false,
    skipAnalysisId = false
  ) {
    let analysisId = widgetQueryOptions.widgetDataSource.analysisId;
    const metadataSelected =
      widgetQueryOptions.customFieldSelected ||
      widgetQueryOptions.standardFieldSelected ||
      widgetQueryOptions.sectionPropertySelected;
    if (widgetQueryOptions.widgetDataSource.type === 'group') {
      analysisId = '*';
    }
    //Split out the filters into metadata and date filters which fall out of analyses and all others
    //Adding to new copied objects so manipulating them below doesn't change the widget filter.
    const analyticItemFilters: AnalyticItemFilterSets = {
      topics: this.createCopy(
        widgetQueryOptions.filters
          ? widgetQueryOptions.filters.topics
          : { and: [], or: [], not: [] }
      ),
      entities: this.createCopy(
        widgetQueryOptions.filters
          ? widgetQueryOptions.filters.entities
          : { and: [], or: [], not: [] }
      ),
      themes: this.createCopy(
        widgetQueryOptions.filters
          ? widgetQueryOptions.filters.themes
          : { and: [], or: [], not: [] }
      ),
      phrases: this.createCopy(
        widgetQueryOptions.filters
          ? widgetQueryOptions.filters.phrases
          : { and: [], or: [], not: [] }
      ),
      dates: { and: [], or: [], not: [] },
      analyses: this.createCopy(
        widgetQueryOptions.filters
          ? widgetQueryOptions.filters.analyses
          : { and: [], or: [], not: [] }
      ),
      metadata: { and: [], or: [], not: [] },
      tags: { and: [], or: [], not: [] },
      core: { and: [], or: [], not: [] },
      taxonomy: this.createCopy(
        widgetQueryOptions.filters
          ? widgetQueryOptions.filters.taxonomy
          : { and: [], or: [], not: [] }
      ),
      sectionProperties: this.createCopy(
        widgetQueryOptions.filters?.sectionProperties ?? { and: [], or: [], not: [] }
      ),
    };

    // Helper function to get the correct filter key for section properties
    const getFilterKey = (analyticItem: string) => {
      return analyticItem?.startsWith('sectionProperties.') ? 'sectionProperties' : analyticItem;
    };

    const analyticItemFilterKey = getFilterKey(widgetQueryOptions.analyticItem);

    //Handle the feature polarities.
    //Remove the filters from the default path.
    //Siphon it off to a new set to merge with the selected item.
    let featurePolarityOrs = [];
    let hasFeaturePolarities = false;
    if (analyticItemFilters[analyticItemFilterKey] && (!forReportFilter || skipFeatureSentiment)) {
      //For polarity charts we'll just use the polarity that is being selected.
      if (!['sentimentPolarity'].includes(widgetQueryOptions.type)) {
        featurePolarityOrs = analyticItemFilters[analyticItemFilterKey].or.filter((filter) => {
          return FilterService.isSentimentPolarityFilter(filter);
        });
        hasFeaturePolarities = featurePolarityOrs.length > 0;
      }
      ['themes', 'topics', 'entities', 'phrases', 'taxonomy'].forEach((item) => {
        analyticItemFilters[item].or = analyticItemFilters[item].or.filter((filter) => {
          return !FilterService.isSentimentPolarityFilter(filter);
        });
      });
    }

    //For calculating the query for items outside of the analyses, we need to remove the Ors calculated here.
    if (forExternalDataOrs) {
      analyticItemFilters.analyses.or = [];
      analyticItemFilters.themes.or = [];
      analyticItemFilters.topics.or = [];
      analyticItemFilters.phrases.or = [];
      analyticItemFilters.entities.or = [];
      analyticItemFilters.taxonomy.or = [];
    }
    let metadataPointQuery;
    let volumeQuery = '';
    let passedAnalyticItem = `analyses.${widgetQueryOptions.analyticItem}`;

    if (!forReportFilter) {
      //Set up the values and paths for the selected analytic item.
      const passedValue = widgetQueryOptions.pointName;

      //Themes are special and use normalizedTitle.  For future normalization changes we can add the updates here.
      // Section properties don't need any suffix - they already have the property name
      if (passedAnalyticItem.includes('themes')) {
        passedAnalyticItem = passedAnalyticItem + '.normalizedTitle';
      } else if (passedAnalyticItem.includes('taxonomy')) {
        passedAnalyticItem = passedAnalyticItem + '.taxonomyFullPath';
      } else if (!passedAnalyticItem.includes('sectionProperties')) {
        passedAnalyticItem = passedAnalyticItem + '.title';
      }

      if (
        widgetQueryOptions.analyticItem &&
        widgetQueryOptions.analyticItem !== 'documents' &&
        widgetQueryOptions.type !== 'numericMetadata'
      ) {
        //If the chart type is anything other than taxonomyMap(which doesn't include an analyticItem) and the analyticItem is null
        //Then we are searching over metadata
        // Exception: section properties should be treated as nested analyses objects, not metadata
        // DASH-10000: Another exception: grid widget with date breakdown should not be treated as simple metadata in the terms of query (generateDateRangeFilter will take care of that)
        if (
          widgetQueryOptions.type !== 'taxonomyMap' &&
          !widgetQueryOptions.analyticItems.includes(widgetQueryOptions.analyticItem) &&
          !widgetQueryOptions.sectionPropertySelected &&
          !widgetQueryOptions.analyticItem?.startsWith('sectionProperties.') &&
          (widgetQueryOptions.type !== 'grid' || !widgetQueryOptions.dateInterval)
        ) {
          const metadataQueryFromValue = (value) => {
            let query = 'metadata.';
            if (FilterService.standardFields.includes(widgetQueryOptions.analyticItem)) {
              query = '';
            }

            // DASH-9952: Treat "<blank>" selections (except userSearch) as missing metadata
            // so the query matches documents where the field does not exist.
            if (
              Utils.unescapeBlank(value) === Constants.BLANK &&
              widgetQueryOptions.analyticItem !== 'userSearch'
            ) {
              return `NOT _exists_:${query}${widgetQueryOptions.analyticItem}`;
            }

            return `${query}${widgetQueryOptions.analyticItem}:"${escapeQuotes(value.toString())}"`;
          };

          if (widgetQueryOptions.type === 'sentimentVolume') {
            const sentimentScale = widgetQueryOptions.sentimentScale;
            const selectedItem = widgetQueryOptions.selectedItems[0];
            metadataPointQuery = metadataQueryFromValue(selectedItem);
            if (passedValue !== 'total') {
              metadataPointQuery += ` AND (analyses.sentimentPolarity${sentimentScale}:"${passedValue}" IN analyses)`;
            }
          } else if (passedValue) {
            metadataPointQuery = metadataQueryFromValue(passedValue);
          }
        }
        //Otherwise we are searching over an analyticItem
        //For drilled down themes we don't want to make an extra query for the item
        //And we don't want to add an extra query if the filters already have it.
        else {
          //For a report filter we have no point selected and just want the filters, so we skip this step.
          //We also want to skip this step for polarity and volume reports which handle the objects analytic items of the filter paradigm
          if (
            ![
              'mentions',
              'taxonony_mentions',
              'topic_phrases',
              'entity_phrases',
              'taxonomy_phrases',
              'taxonomy_themes',
            ].includes(widgetQueryOptions.drilledDownOn) &&
            !['sentimentVolume', 'sentimentPolarity', 'featureScoring', 'taxonomyMap'].includes(
              widgetQueryOptions.type
            )
          ) {
            if (
              widgetQueryOptions.drilledDownOn !== 'themes' &&
              analyticItemFilters[analyticItemFilterKey] &&
              !analyticItemFilters[analyticItemFilterKey].and.some(
                (filter) =>
                  filter.value === passedValue &&
                  filter.analyticItem in [passedAnalyticItem, 'ML-' + passedAnalyticItem]
              )
            ) {
              let subValue = '';
              if (
                widgetQueryOptions.analyticItem.includes('entities') ||
                widgetQueryOptions.analyticItem.includes('topics')
              ) {
                if (widgetQueryOptions.subType) {
                  subValue = widgetQueryOptions.subType;
                }
              }

              if (passedValue) {
                if (subValue.length) {
                  analyticItemFilters[analyticItemFilterKey].and.push({
                    analyticItem: passedAnalyticItem,
                    value: passedValue,
                    subLabel: subValue,
                    kind: 'pointFilter',
                  });
                } else {
                  analyticItemFilters[analyticItemFilterKey].and.push({
                    analyticItem: passedAnalyticItem,
                    value: passedValue,
                    kind: 'pointFilter',
                  });
                }
              }
            }
          }
        }
      }

      //For sentiment volume we really care about the results of the polarity chosen for a specific field.
      //For this to work we're removing the extra polarity filters as they all should only relate to the chosen value.
      //Not like a normal chart where the filters for analyticItem polarity is for ANY instance of a certain polarity across all items
      //Sentiment volume is for ONLY the instance of the chosen value with a certain polarity.
      if (
        (widgetQueryOptions.type === 'sentimentVolume' || hasFeaturePolarities) &&
        !metadataSelected
      ) {
        if (widgetQueryOptions.pointName === 'total' || hasFeaturePolarities) {
          //We need to explicitly set up the filters if they are item polarity based here (see above).
          //Otherwise we'll be getting results across the entire data set for all items.
          const itemPolarityFilters = { and: [], or: [], not: [] };
          itemPolarityFilters.and = analyticItemFilters[analyticItemFilterKey].and.filter(
            (filter) => {
              return FilterService.isSentimentPolarityFilter(filter);
            }
          );
          itemPolarityFilters.or = analyticItemFilters[analyticItemFilterKey].or.filter(
            (filter) => {
              return FilterService.isSentimentPolarityFilter(filter);
            }
          );
          itemPolarityFilters.not = analyticItemFilters[analyticItemFilterKey].not.filter(
            (filter) => {
              return FilterService.isSentimentPolarityFilter(filter);
            }
          );

          //We don't want to use the traffic light polarities if a specific polarity is being selected
          //on a sentiment by volume chart.
          if (
            widgetQueryOptions.type === 'sentimentVolume' &&
            widgetQueryOptions.pointName !== 'total'
          ) {
            hasFeaturePolarities = false;
          } else {
            featurePolarityOrs.forEach((element) => {
              itemPolarityFilters.or.push(element);
            });
          }
          const volumeQueryParts = [];
          const andQueryParts = [];
          for (const filter of itemPolarityFilters.and) {
            andQueryParts.push(
              `( ${filter.analyticItem}:"${escapeQuotes(
                filter.value
              )}" AND ${passedAnalyticItem}:"${escapeQuotes(
                widgetQueryOptions.selectedItems[0]
              )}" IN analyses.${widgetQueryOptions.analyticItem})`
            );
          }
          if (andQueryParts.length > 0) {
            volumeQueryParts.push(andQueryParts.join(' AND '));
          }

          const orQueryParts = [];

          for (const filter of itemPolarityFilters.or) {
            orQueryParts.push(`${filter.analyticItem}:"${filter.value}"`);
          }

          if (orQueryParts.length > 0) {
            let orQuery = '';
            orQuery = orQueryParts.join(' OR ');
            if (
              !widgetQueryOptions.drilledDownOn ||
              widgetQueryOptions.drilledDownOn === 'none' ||
              widgetQueryOptions.type === 'taxonomyMap'
            ) {
              orQuery = `((${orQuery}) AND ${passedAnalyticItem}:"${escapeQuotes(
                hasFeaturePolarities && widgetQueryOptions.type !== 'sentimentVolume'
                  ? passedValue
                  : widgetQueryOptions.selectedItems[0]
              )}" IN analyses.${widgetQueryOptions.analyticItem})`;
            } else {
              if (orQueryParts.length > 1) {
                orQuery = `(${orQuery})`;
              }
            }
            volumeQueryParts.push(orQuery);
          }

          const notQueryParts = [];
          for (const filter of itemPolarityFilters.not) {
            notQueryParts.push(
              `NOT ( ${filter.analyticItem}:"${
                filter.value
              }" AND ${passedAnalyticItem}:"${escapeQuotes(
                hasFeaturePolarities && widgetQueryOptions.type !== 'sentimentVolume'
                  ? passedValue
                  : widgetQueryOptions.selectedItems[0]
              )}" IN analyses.${widgetQueryOptions.analyticItem})`
            );
          }
          if (notQueryParts.length > 0) {
            volumeQueryParts.push(notQueryParts.join(' AND '));
          }

          if (volumeQueryParts.length > 0) {
            volumeQuery = volumeQueryParts.join(' AND ');
          }
          if (volumeQuery.trim().length > 1 && widgetQueryOptions.type === 'sentimentVolume') {
            volumeQuery += ` AND (${passedAnalyticItem}:"${escapeQuotes(
              widgetQueryOptions.selectedItems[0]
            )}" IN analyses.${widgetQueryOptions.analyticItem})`;
          }
        }

        //Don't let the filter engine handle the queries involving polarity for the item.
        analyticItemFilters[analyticItemFilterKey].and = analyticItemFilters[
          analyticItemFilterKey
        ].and.filter((filter) => {
          return !FilterService.isSentimentPolarityFilter(filter);
        });
        analyticItemFilters[analyticItemFilterKey].or = analyticItemFilters[
          analyticItemFilterKey
        ].or.filter((filter) => {
          return !FilterService.isSentimentPolarityFilter(filter);
        });
        analyticItemFilters[analyticItemFilterKey].not = analyticItemFilters[
          analyticItemFilterKey
        ].not.filter((filter) => {
          return !FilterService.isSentimentPolarityFilter(filter);
        });
      }
    }

    // DASH-9694: Let's get the comparison section filters. This will be used for multipleSubtypeFilters in order to build the appropriate query.
    const comparisonSectionFilters: FilterSet =
      FilterUtils.getComparisonSectionFilters(analyticItemFilters);
    // Here we prepare a separate query in case of multiple subtypes selected
    // Currently this is the only way to assemble a 'proper' query string
    const multipleSubtype = this.getFilterWithMultipleSubtype(analyticItemFilters);
    const multipleSubtypeFilters = [];
    multipleSubtype.forEach((mst) => {
      analyticItemFilters[mst.filterSet].and = analyticItemFilters[mst.filterSet].and.filter(
        (f) =>
          f.value !== mst.filter.value ||
          f.analyticItem !== mst.filter.analyticItem ||
          f.subLabel !== mst.filter.subLabel
      );

      const orFilter = FilterService.getDefaultFilterSet();
      orFilter[mst.filterSet].or = mst.filter.subLabel.split(',').map((sub) => ({
        analyticItem: mst.filter.analyticItem,
        value: mst.filter.value,
        subLabel: sub,
      }));
      // DASH-9694: Apply the comparison section filters to the multipleSubtypeFilter.
      FilterUtils.applyFilterSet(comparisonSectionFilters, orFilter, 'sectionProperties');

      multipleSubtypeFilters.push(orFilter);
    });

    //Let the filter service do it's inital work on the filters
    let query = this.buildQueryString(analyticItemFilters, widgetQueryOptions);
    if (!query) {
      query = '';
    }

    //Run point query,  This is passing in the formatted otherFilters and a volumeQuery if this is
    //Sentiment vs volume.
    //Again no point query to generate if this is being called for a report.
    if (!forReportFilter) {
      query = this.pointQuery(
        widgetQueryOptions,
        query,
        analyticItemFilters,
        volumeQuery,
        analysisId,
        hasFeaturePolarities
      );
    } else {
      if (query) {
        if (analysisId && !skipAnalysisId) {
          query = `(analyses.analysisName: "${analysisId}" AND ${query} IN analyses)`;
        }
      } else {
        if (widgetQueryOptions.widgetDataSource.type === 'analysis' && !skipAnalysisId) {
          query = `(analyses.analysisName: "${analysisId}" IN analyses)`;
        }
      }
    }

    multipleSubtypeFilters.forEach((mstf) => {
      query = `${this.buildQueryString(mstf, widgetQueryOptions)} AND ${query}`;
    });

    // date filtering
    if (
      widgetQueryOptions.widgetContextMenu &&
      widgetQueryOptions.type !== 'taxonomyMap' &&
      widgetQueryOptions.type !== 'scatterPlot' &&
      typeof widgetQueryOptions.widgetContextMenu.category === 'number' &&
      !forReportFilter
    ) {
      const daterange = this.generateDateRangeFilter(
        widgetQueryOptions.widgetContextMenu.category,
        widgetQueryOptions.widgetContextMenu.index,
        widgetQueryOptions.widgetContextMenu.series.data,
        widgetQueryOptions.dateInterval
      );
      if (daterange) {
        let dateField = 'createdAt';
        if (
          widgetQueryOptions.type === 'grid' &&
          FieldService.isCustomField(widgetQueryOptions.analyticItem)
        ) {
          dateField = `metadata.${widgetQueryOptions.analyticItem}`;
        }

        query += ` AND ${this.generateDateRangeQueryPart(dateField, daterange)}`;
      }
    }

    //Data external to the analysis needs to be calculated in it's own section.
    //If we're in that method already (which calls this one again to generate a subform of the full query) skip this call.
    if (!forExternalDataOrs) {
      query = this.calculateAnalysisExternalQuerySection(
        widgetQueryOptions,
        query,
        forReportFilter
      );
    }

    //Parent metadata for the cross column report
    if (widgetQueryOptions.parentIsMetadata && !forReportFilter) {
      let parentMetaDataQueryPrefix;
      if (FilterService.standardFields.includes(widgetQueryOptions.parentAnalyticItem)) {
        parentMetaDataQueryPrefix = '';
      } else {
        parentMetaDataQueryPrefix = 'metadata.';
      }

      const ppq = [];
      widgetQueryOptions.parentPointName.split('|+|').forEach((pst) => {
        const parentMetaDataQuery =
          parentMetaDataQueryPrefix +
          widgetQueryOptions.parentAnalyticItem +
          ':' +
          '"' +
          escapeQuotes(pst.toString()) +
          '"';
        ppq.push(parentMetaDataQuery);
      });

      if (typeof metadataPointQuery !== 'undefined') {
        metadataPointQuery = `${metadataPointQuery} AND (${ppq.join(' OR ')})`;
      } else {
        metadataPointQuery = `(${ppq.join(' OR ')})`;
      }
    }

    //If a custom field was the item drilled into then add it to the query calculated for filters.
    if (metadataPointQuery) {
      //Selecting a specific range of the breakdown/donut widget for top box
      if (widgetQueryOptions.boxScoringQueryOptions) {
        let breakdDownPointQuery = '';

        const numericFieldId = widgetQueryOptions.boxScoringQueryOptions.fieldId;
        const min = widgetQueryOptions.boxScoringQueryOptions.min;
        const max = widgetQueryOptions.boxScoringQueryOptions.max;
        const topBoxSize = parseInt(widgetQueryOptions.boxScoringQueryOptions['topBoxSize'], 10);
        const bottomBoxSize = parseInt(
          widgetQueryOptions.boxScoringQueryOptions['bottomBoxSize'],
          10
        );
        const topRange = max - topBoxSize + 1;
        const bottomRange = min + bottomBoxSize;

        let boxSection: string;
        switch (widgetQueryOptions.boxScoringQueryOptions.type) {
          case 'donut':
            boxSection = widgetQueryOptions.widgetContextMenu.name.toLowerCase();
            break;
          case 'breakdown':
            boxSection = widgetQueryOptions.widgetContextMenu.series.name.toLowerCase();
            break;
        }

        switch (boxSection) {
          case 'top':
            breakdDownPointQuery =
              'metadata.' + numericFieldId + ': [ ' + topRange + ' TO ' + (max + 1) + ' ] AND ';
            break;
          case 'neutral':
            breakdDownPointQuery =
              'metadata.' +
              numericFieldId +
              ': [ ' +
              bottomRange +
              ' TO ' +
              (topRange - 1) +
              ' ] AND ';
            break;
          case 'bottom':
            breakdDownPointQuery =
              'metadata.' + numericFieldId + ': [ ' + min + ' TO ' + (bottomRange - 1) + ' ] AND ';
            break;
        }

        // for donut widget we don't need the first part
        query =
          widgetQueryOptions.boxScoringQueryOptions.type === 'breakdown'
            ? metadataPointQuery + ' AND ' + breakdDownPointQuery + query
            : breakdDownPointQuery + query;
      } else if (widgetQueryOptions.type === 'gauge') {
        query = `_exists_:${metadataPointQuery.split(':')[0]} AND (${query})`;
      } else {
        query = `${metadataPointQuery} AND (${query})`;
      }
    }

    if (widgetQueryOptions.bucketScoringQueryOptions) {
      const { fieldId, minimumValue, maximumValue, includeMinimum, includeMaximum } =
        widgetQueryOptions.bucketScoringQueryOptions;

      const prefix = FilterService.standardFields.includes(fieldId) ? '' : 'metadata.';
      const rangeStart = includeMinimum ? '[' : '{';
      const rangeEnd = includeMaximum ? ']' : '}';
      const bucketPointQuery =
        prefix +
        fieldId +
        ': ' +
        rangeStart +
        ' ' +
        minimumValue +
        ' TO ' +
        maximumValue +
        ' ' +
        rangeEnd;

      query = `${bucketPointQuery} AND ${query}`;
    }

    if (widgetQueryOptions.userQuery) {
      query = `(${widgetQueryOptions.userQuery}) AND ${query}`;
    }

    const userSearchGlobalString = this.buildUserSearchString(widgetQueryOptions, true);
    if (userSearchGlobalString) {
      query = `(${userSearchGlobalString}) AND ${query}`;
    }

    if (widgetQueryOptions.contentChannelFilter?.length) {
      const chFilter = widgetQueryOptions.contentChannelFilter
        .map((channel) => `_contentChannelId_:${channel}`)
        .join(' OR ');
      query = `(${chFilter}) AND ${query}`;
    }

    return query;
  }

  generateDateRangeQueryPart(
    analyticItem: string,
    daterange: { fromDate: Moment; toDate: Moment }
  ): string {
    return `${analyticItem}:[${daterange.fromDate.format(
      DATE_TIME_WITHOUT_TIMEZONE_FORMAT
    )} TO ${daterange.toDate.format(DATE_TIME_WITHOUT_TIMEZONE_FORMAT)}]`;
  }

  generateDateRangeFilter(timestamp: number, dataIndex: number, data: any[], dateInterval: string) {
    try {
      //There were issues where early indicies could vary by multiple interval steps between data.
      //Now get the indicies based on the current index and the next index, and if we're on the last index just set the
      //step to the value of the interval.
      const sortedData = Array.isArray(data) ? [...data].sort((a, b) => a.x - b.x) : data;
      const step =
        dataIndex < sortedData.length - 1
          ? sortedData[dataIndex + 1].x - sortedData[dataIndex].x
          : FilterService.defaultDateIntervalStep(dateInterval ?? 'day');
      const fromDate = moment.tz(timestamp, this.timezoneService.timezone);
      const toDate = moment
        .tz(timestamp + step, this.timezoneService.timezone)
        .subtract(1, 'millisecond');

      return { fromDate, toDate };
    } catch (err) {
      console.log(err);
    }
  }

  private getFilterWithMultipleSubtype(filters): { filterSet; filter }[] {
    const ret = [];

    if (filters) {
      for (const filterSet of Object.keys(filters)) {
        for (const filter of filters[filterSet].and) {
          if (filter.subLabel && filter.subLabel.includes(',')) {
            ret.push({ filterSet, filter });
          }
        }
      }
    }

    return ret;
  }

  //Method to calculate the section of the query for items that fall outside of the analysis
  //They need to still be ANDed with the analysisName and other relevant filters (and point data for drilldown)
  public calculateAnalysisExternalQuerySection(
    widgetQueryOptions: WidgetQueryOptions,
    query: string,
    forReportFilter: boolean
  ): string {
    let calculatedQuery;

    //Splitting out the filters that are ANDs and NOTs as these and the ORs need their own sections.
    const nonAnalyticItemFilters_AND_NOT: AnalyticItemFilterSets = {
      topics: { and: [], or: [], not: [] },
      entities: { and: [], or: [], not: [] },
      themes: { and: [], or: [], not: [] },
      phrases: { and: [], or: [], not: [] },
      dates: this.createCopy(
        widgetQueryOptions.filters ? widgetQueryOptions.filters.dates : { and: [], or: [], not: [] }
      ),
      analyses: { and: [], or: [], not: [] },
      metadata: this.createCopy(
        widgetQueryOptions.filters
          ? widgetQueryOptions.filters.metadata
          : { and: [], or: [], not: [] }
      ),
      tags: this.createCopy(
        widgetQueryOptions.filters ? widgetQueryOptions.filters.tags : { and: [], or: [], not: [] }
      ),
      core: this.createCopy(
        widgetQueryOptions.filters ? widgetQueryOptions.filters.core : { and: [], or: [], not: [] }
      ),
      taxonomy: { and: [], or: [], not: [] },
      sectionProperties: { and: [], or: [], not: [] },
    };

    //Remove the ORs
    nonAnalyticItemFilters_AND_NOT.dates.or = [];
    nonAnalyticItemFilters_AND_NOT.metadata.or = [];
    nonAnalyticItemFilters_AND_NOT.tags.or = [];
    nonAnalyticItemFilters_AND_NOT.core.or = [];

    //Select for all of the ORs of the external filters
    const nonAnalyticItemFilters_OR: AnalyticItemFilterSets = {
      topics: { and: [], or: [], not: [] },
      entities: { and: [], or: [], not: [] },
      themes: { and: [], or: [], not: [] },
      phrases: { and: [], or: [], not: [] },
      dates: this.createCopy(
        widgetQueryOptions.filters ? widgetQueryOptions.filters.dates : { and: [], or: [], not: [] }
      ),
      analyses: { and: [], or: [], not: [] },
      metadata: this.createCopy(
        widgetQueryOptions.filters
          ? widgetQueryOptions.filters.metadata
          : { and: [], or: [], not: [] }
      ),
      tags: this.createCopy(
        widgetQueryOptions.filters ? widgetQueryOptions.filters.tags : { and: [], or: [], not: [] }
      ),
      core: this.createCopy(
        widgetQueryOptions.filters ? widgetQueryOptions.filters.core : { and: [], or: [], not: [] }
      ),
      taxonomy: { and: [], or: [], not: [] },
      sectionProperties: { and: [], or: [], not: [] },
    };

    //Remove the ANDs and NOTs
    nonAnalyticItemFilters_OR.dates.and = [];
    nonAnalyticItemFilters_OR.dates.not = [];
    nonAnalyticItemFilters_OR.metadata.and = [];
    nonAnalyticItemFilters_OR.metadata.not = [];
    nonAnalyticItemFilters_OR.tags.and = [];
    nonAnalyticItemFilters_OR.tags.not = [];
    nonAnalyticItemFilters_OR.core.and = [];
    nonAnalyticItemFilters_OR.core.not = [];

    //Calculate both of the relevant query strings via the filter service
    const metadataDateQuery_AND_NOT = this.buildQueryString(nonAnalyticItemFilters_AND_NOT);
    const metadataDateQuery_OR = this.buildQueryString(nonAnalyticItemFilters_OR);

    //If there are no queries generated, then just return the base query.
    if (!metadataDateQuery_AND_NOT && !metadataDateQuery_OR) {
      return query;
    }

    //The metadate, tags,  and date query will always be put outside of the query that falls in the analyses.analysisName
    //Here we add the ANDs and NOTs generated by the filter service with the initial query being generated by
    //getQueryForDocuments
    if (metadataDateQuery_AND_NOT) {
      calculatedQuery = `(${metadataDateQuery_AND_NOT}${query ? ' AND ' + query : ''})`;
    }

    //Now we handle to ORs.
    //For the ORs to work properly, we need to combine the OR clause generated by the filterService above with the
    //rest of the filters, minus the ORs because they were already calculated in the base query.
    //This isn't the simplest form logically, but for translation to ES it works smoother.

    if (metadataDateQuery_OR) {
      let eor = '';

      //EOR is a special OR for use with Elastic.  For there to be two separate nested blocks we need something that behaves
      //like a normal OR but won't cut off before the final nesting.
      //We add it in if there is an original query being passed along.

      const hasAnalyticOr = Object.keys(widgetQueryOptions.filters)
        .filter((key) =>
          ['topics', 'entities', 'themes', 'phrases', 'taxonomy', 'analyses'].includes(key)
        )
        .some((key) =>
          widgetQueryOptions.filters[key].or.some(
            (filter) => !filter.analyticItem.includes('sentimentPolarity')
          )
        );

      //If there are no analytic item ORs then we don't need to use the EOR to create the properly nested elasticsearch ORs
      //and can just compare against the base query.
      if (hasAnalyticOr) {
        if (calculatedQuery) {
          eor = ` EOR ${calculatedQuery}`;
        } else if (query) {
          eor = ` EOR (${query})`;
        }
      }

      //Now we caclulate the base query again, but with no ORs attached to it.
      const analyticItemsQueryNoOr = this.getQueryForDocuments(
        widgetQueryOptions,
        true,
        forReportFilter
      );

      //If there is an AND_NOT section add it into the OriginalNoORd query so we're framing the external ors against he fully needed
      //nested paths in elasticsearch
      calculatedQuery = `((${metadataDateQuery_OR} AND (${analyticItemsQueryNoOr}))${eor})`;
      if (metadataDateQuery_AND_NOT) {
        calculatedQuery = `((${metadataDateQuery_OR} AND (${metadataDateQuery_AND_NOT} AND ${analyticItemsQueryNoOr}))${eor})`;
      }
    }

    return calculatedQuery;
  }

  //Method to handle the extra parts needed for "show documents" queries that the filter service doesn't handle.
  //Does the following
  //1) Wraps the filter service query in the analysisName for proper nesting (this is done in the java code for the filters on reports)
  //2) Handles the specific queries for taxonomy, theme, and mention drill down
  //3) Handles polarity so that the polarity from the selected point doesn't interfere with, and can have a different polarity from the filters.
  public pointQuery(
    widgetQueryOptions: WidgetQueryOptions,
    passedFilterQuery: string,
    otherFilters: AnalyticItemFilterSets,
    volumeQuery: string,
    analysisId: string,
    featurePolarityFilters = false
  ): string {
    // Handle section properties as analytic items
    let path: string;
    if (
      widgetQueryOptions.sectionPropertySelected ||
      widgetQueryOptions.analyticItem?.startsWith('sectionProperties.')
    ) {
      path = `analyses.flatSections`;
    } else {
      path =
        widgetQueryOptions.analyticItem && widgetQueryOptions.analyticItem !== null
          ? `analyses.${widgetQueryOptions.analyticItem}`
          : 'analyses.topics';
    }
    const queryParts = [];
    let queryExtra = '';
    //Analytic Item Mentions
    if (widgetQueryOptions.drilledDownOn === 'mentions') {
      queryParts.push(`${path}.title:"${escapeQuotes(widgetQueryOptions.drilledDownLocation)}"`);
      queryExtra = `(${path}.mentionLabels:"${escapeQuotes(
        widgetQueryOptions.pointName
      )}" IN ${path})`;
    }

    //Analytic Item Mentions
    if (
      widgetQueryOptions.drilledDownOn === 'topic_phrases' ||
      widgetQueryOptions.drilledDownOn === 'entity_phrases'
    ) {
      queryParts.push(`${path}.title:"${escapeQuotes(widgetQueryOptions.drilledDownLocation)}"`);
      queryExtra = `(${path}.phrases.phrase.title:"${escapeQuotes(
        widgetQueryOptions.pointName
      )}" IN ${path}.phrases.phrase)`;
    }

    //Taxonomy Mentions, Phrases, Themes
    else if (
      widgetQueryOptions.drilledDownOn === 'taxonomy_mentions' ||
      widgetQueryOptions.drilledDownOn === 'taxonomy_phrases' ||
      widgetQueryOptions.drilledDownOn === 'taxonomy_themes'
    ) {
      //When showing mentions of a top level node we need to hack in the taxonomy path
      //This handles the queries in that case
      const topLevelFix =
        widgetQueryOptions.taxonomyPath === widgetQueryOptions.drilledDownLocation
          ? widgetQueryOptions.taxonomyPath
          : widgetQueryOptions.taxonomyPath + '|' + widgetQueryOptions.drilledDownLocation;
      const tempPath = 'analyses.' + widgetQueryOptions.widgetContextMenu.entityType;
      if (widgetQueryOptions.drilledDownOn === 'taxonomy_themes') {
        queryExtra =
          `((analyses.topics.taxonomyNodePaths:"${topLevelFix}" AND (analyses.topics.themes.normalizedTitle:"${escapeQuotes(
            widgetQueryOptions.pointName
          )}" IN analyses.topics.themes) IN analyses.topics) OR ` +
          `(analyses.entities.taxonomyNodePaths:"${topLevelFix}" AND (analyses.entities.themes.normalizedTitle:"${escapeQuotes(
            widgetQueryOptions.pointName
          )}" IN analyses.entities.themes) IN analyses.entities))`;
      } else {
        queryExtra =
          widgetQueryOptions.drilledDownOn === 'taxonomy_mentions'
            ? `(${tempPath}.mentionLabels:"${widgetQueryOptions.pointName}" IN ${tempPath})`
            : `(${tempPath}.phrases.phrase.title:"${escapeQuotes(
                widgetQueryOptions.pointName
              )}" IN ${tempPath}.phrases.phrase)`;
        queryExtra = `${tempPath}.taxonomyNodePaths:"${topLevelFix}" AND ${queryExtra} IN ${tempPath}`;
      }

      if (widgetQueryOptions.taxonomyPath && widgetQueryOptions.taxonomyPath.length > 0) {
        const taxonomyPathQuery = `/${FilterUtils.escapeTaxonomyStartingWith(
          widgetQueryOptions.taxonomyPath
        )}/`;
        queryParts.push(
          `(analyses.taxonomy.taxonomyFullPath:${taxonomyPathQuery} IN analyses.taxonomy)`
        );
      }
    }
    //Drilled into themes
    else if (widgetQueryOptions.drilledDownOn === 'themes') {
      queryParts.push(
        `${path}.title:` +
          FilterUtils.computeValue(
            `${path}.title`,
            widgetQueryOptions.drilledDownLocation,
            this.getFilterConfigItem(`${path}.title`)
          )
      );
      queryExtra = `(${path}.themes.normalizedTitle:"${escapeQuotes(
        widgetQueryOptions.pointName
      )}" IN ${path}.themes)`;
    }
    //For a taxonomy that isn't at the mention level we include the fully formed taxonomy path and title
    else if (
      widgetQueryOptions.type === 'taxonomyMap' &&
      (!widgetQueryOptions.drilledDownOn ||
        widgetQueryOptions.drilledDownOn === 'none' ||
        widgetQueryOptions.drilledDownOn === 'taxonomy')
    ) {
      //Wrapped with parens and include the IN for proper nesting
      //If the depth is >=1 we want to include the taxonomy path in the search to not
      //get results from another taxonomy node
      let taxonomyPoint = widgetQueryOptions.pointName;
      if (widgetQueryOptions.taxonomyPath && widgetQueryOptions.taxonomyPath.length >= 0) {
        taxonomyPoint = `${widgetQueryOptions.taxonomyPath}|${taxonomyPoint}`;
      }
      const taxonomyPointQuery = `/${FilterUtils.escapeTaxonomyStartingWith(taxonomyPoint)}/`;
      queryParts.push(
        `(analyses.taxonomy.taxonomyFullPath:${taxonomyPointQuery} IN analyses.taxonomy)`
      );
    }

    // Handle fixedPath for section properties
    let fixedPath: string;
    let fixedFilterValue = `"${escapeQuotes(widgetQueryOptions.pointName)}"`;
    if (
      widgetQueryOptions.sectionPropertySelected ||
      widgetQueryOptions.analyticItem?.startsWith('sectionProperties.')
    ) {
      const propertyName =
        widgetQueryOptions.analyticItem?.replace('sectionProperties.', '') ||
        widgetQueryOptions.analyticItem;
      fixedPath = `${path}.${propertyName}`;
    } else {
      if (path === 'analyses.themes') {
        fixedPath = `${path}.normalizedTitle`;
      } else if (path === 'analyses.taxonomy') {
        fixedPath = `${path}.taxonomyFullPath`;
      } else {
        fixedPath = `${path}.title`;
      }
    }
    //If this is sentiment polarity then add the query parts for both the polarity value and the field so they are
    //combined together.
    if (widgetQueryOptions.type === 'sentimentPolarity') {
      const sentimentScale = widgetQueryOptions.sentimentScale;

      let sentiment = widgetQueryOptions.widgetContextMenu.series.name.toLowerCase();
      if (sentiment === 'document_sentiment_polarity') {
        sentiment = widgetQueryOptions.pointName.toLowerCase();
      }

      //Custom and standard fields will not include the analyses feature path.
      const feature = path.split('.')[1];
      // DASH-10430 (DASH-10254): The default behavior should always be to use regex,
      // and direct hits should only be used when clicking on a point.
      if (feature === 'taxonomy' && !FilterUtils.hasInSectionsFilter(widgetQueryOptions)) {
        fixedFilterValue = `/${FilterUtils.escapeTaxonomyStartingWith(widgetQueryOptions.pointName)}/`;
      }

      if (
        ['themes', 'phrases', 'topics', 'entities', 'taxonomy', 'flatSections'].includes(feature)
      ) {
        queryParts.push(`${fixedPath}:${fixedFilterValue}`);
        if (['entities', 'topics'].includes(feature) && widgetQueryOptions.subType) {
          const entityTypeFilter = widgetQueryOptions.subType.split(',');
          let typeAttr = feature === 'entities' ? 'entityType' : 'type';

          const entityTypeQuery = [];

          entityTypeFilter.forEach((etf) => {
            typeAttr = feature === 'entities' ? 'entityType' : 'type';
            if (FilterService.clauseModelTypes.includes(etf) && feature === 'topics') {
              typeAttr = 'purpose';
            }
            entityTypeQuery.push(`${path}.${typeAttr}:"${escapeQuotes(etf)}"`);
          });

          queryParts.push(`(${entityTypeQuery.join(' OR ')})`);
        }

        queryExtra = `${path}.sentimentPolarity${sentimentScale}:"${sentiment}"`;
      } else {
        queryParts.push(`analyses.sentimentPolarity${sentimentScale}:"${sentiment}"`);
        path = 'analyses';
      }
    }

    let volumeSkipIn = false;
    //For sentiment volume we have to specifiy the field value based on selectedItem
    //since the pointname is the polarity.  If it is 'total' then we don't add the polarity bit.
    //We also don't want this calculation if the field is metadata.
    //In that case this will be calculated inside the metadataPointQuery section of calculateQueryForDocuments.
    if (
      widgetQueryOptions.type === 'sentimentVolume' &&
      !(
        widgetQueryOptions.customFieldSelected ||
        widgetQueryOptions.standardFieldSelected ||
        widgetQueryOptions.sectionPropertySelected
      )
    ) {
      const sentimentScale = widgetQueryOptions.sentimentScale;

      const selectedItem = escapeQuotes(widgetQueryOptions.selectedItems[0]);
      if (widgetQueryOptions.pointName !== 'total') {
        queryParts.push(`${fixedPath}:"${selectedItem}"`);
        queryExtra = `${path}.sentimentPolarity${sentimentScale}:"${escapeQuotes(
          widgetQueryOptions.pointName
        )}"`;
      } else {
        if (volumeQuery.trim().length > 0) {
          queryParts.push(volumeQuery);
          volumeSkipIn = true;
        } else {
          queryParts.push(`${fixedPath}:"${selectedItem}"`);
        }
      }
    }

    //Use the volumeQuery generated for feature polarity filters.
    if (featurePolarityFilters && !volumeSkipIn) {
      queryParts.push(volumeQuery);
      if (widgetQueryOptions.type !== 'sentimentVolume') {
        volumeSkipIn = true;
      }
    }

    // Pass in the parent slice for the 'other' element for crosstab
    if (
      widgetQueryOptions.type === 'crosstab' &&
      widgetQueryOptions.parentAnalyticItem &&
      !widgetQueryOptions.parentIsMetadata
    ) {
      // Handle section properties for parent (column)
      if (widgetQueryOptions.parentSectionPropertySelected) {
        // Use flatSections path for section properties
        path = `analyses.flatSections`;
        const propertyName = widgetQueryOptions.parentAnalyticItem
          .replace('sectionProperties.', '')
          .replace('analyses.sectionProperties.', '')
          .replace('analyses.', '');

        const ppq = [];
        widgetQueryOptions.parentPointName.split('|+|').forEach((pst) => {
          ppq.push(`${path}.${propertyName}:"${escapeQuotes(pst)}"`);
        });

        // Combine parent property with parent section filters if they exist
        if (widgetQueryOptions.parentSelectedSections?.length) {
          const parentSectionFilterQuery = this.getGlobalSectionListQueryPart(
            widgetQueryOptions.parentSelectedSections
          );
          // Extract just the filter conditions without the IN wrapper
          const filterConditions = parentSectionFilterQuery
            .replace(/^\(/, '')
            .replace(/ IN analyses\.flatSections\)$/, '');
          // Combine property and filters in one IN analyses.flatSections wrapper
          // Include the IN wrapper here so it doesn't get double-wrapped later
          queryParts.push(`((${ppq.join(' OR ')}) AND ${filterConditions} IN ${path})`);
        } else {
          queryParts.push(`(${ppq.join(' OR ')} IN ${path})`);
        }
      } else {
        // Normal analytic item handling
        path = `analyses.${widgetQueryOptions.parentAnalyticItem}`;

        const isParentTaxonomy = widgetQueryOptions.parentAnalyticItem.includes('taxonomy');
        let titleAttr = 'title';
        if (widgetQueryOptions.parentAnalyticItem.includes('themes')) {
          titleAttr = 'normalizedTitle';
        } else if (isParentTaxonomy) {
          titleAttr = 'taxonomyFullPath';
        }

        const ppq = [];

        widgetQueryOptions.parentPointName.split('|+|').forEach((pst) => {
          // DASH-10430 (DASH-10254): The default behavior should always be to use regex,
          // and direct hits should only be used when clicking on a point.
          let fixedFilterValueForParent = `"${escapeQuotes(pst)}"`;
          if (isParentTaxonomy && !FilterUtils.hasParentInSectionsFilter(widgetQueryOptions)) {
            fixedFilterValueForParent = `/${FilterUtils.escapeTaxonomyStartingWith(pst)}/`;
          }

          ppq.push(`${path}.${titleAttr}:${fixedFilterValueForParent}`);
        });

        queryParts.push(`(${ppq.join(' OR ')})`);

        if (widgetQueryOptions.parentSubType && widgetQueryOptions.parentSubType.length) {
          const parentSubType = widgetQueryOptions.parentSubType.split(',');
          let typeAttr =
            widgetQueryOptions.parentAnalyticItem === 'entities' ? 'entityType' : 'type';
          const entityTypeQuery = [];

          parentSubType.forEach((pst) => {
            typeAttr = widgetQueryOptions.parentAnalyticItem === 'entities' ? 'entityType' : 'type';
            if (
              FilterService.clauseModelTypes.includes(pst) &&
              widgetQueryOptions.parentAnalyticItem === 'topics'
            ) {
              typeAttr = 'purpose';
            }
            entityTypeQuery.push(`${path}.${typeAttr}:"${escapeQuotes(pst)}"`);
          });

          queryParts.push(`(${entityTypeQuery.join(' OR ')})`);
        }

        if (widgetQueryOptions.parentSelectedSections?.length) {
          const parentSectionQuery = this.getAnalyticItemSectionInfoListQueryPart(
            widgetQueryOptions.parentAnalyticItem,
            widgetQueryOptions.parentSelectedSections,
            null
          );
          queryParts.push(parentSectionQuery);
        }
      }
    }

    let query = queryParts.length >= 1 ? queryParts.join(' AND ') : '';

    //Here is the main magic for proper nesting
    //queryExtra will already have it's proper IN, which will always be
    //inside of the ${path} because they queryExtra's IN is derived from ${path}
    //So all that's left is to put the query inside of it's proper nesting level
    //Each option is wrapped in parens so each point query is it's own unit
    if (
      queryParts.length >= 1 &&
      (!volumeSkipIn ||
        widgetQueryOptions.drilledDownOn === 'topic_phrases' ||
        widgetQueryOptions.drilledDownOn === 'mentions' ||
        widgetQueryOptions.drilledDownOn === 'entity_phrases' ||
        widgetQueryOptions.drilledDownOn === 'themes')
    ) {
      if (queryExtra) {
        if (
          widgetQueryOptions.drilledDownOn === 'taxonomy_mentions' ||
          widgetQueryOptions.drilledDownOn === 'taxonomy_phrases' ||
          widgetQueryOptions.drilledDownOn === 'taxonomy_themes'
        ) {
          query = `${query} AND (${queryExtra})`;
        } else {
          if (widgetQueryOptions?.selectedSection?.length) {
            const sectionQuery = this.getAnalyticItemSectionInfoListQueryPart(
              widgetQueryOptions.analyticItem,
              widgetQueryOptions.selectedSection,
              null
            );
            queryExtra = `${queryExtra} AND ${sectionQuery}`;
          }

          query = `(${query} AND ${queryExtra} IN ${path})`;
        }
      } else {
        // Skip wrapping if query already contains IN analyses.flatSections (for section properties)
        if (path === 'analyses.flatSections' && query.includes('IN analyses.flatSections')) {
          // Query is already properly wrapped, don't double-wrap
          query = `(${query})`;
        } else {
          query = `(${query} IN ${path})`;
        }
      }
    }

    //Adding the filter queries to the additional query info processed in this method.
    if (passedFilterQuery) {
      if (query.trim().length > 0 && !query.includes('*:*')) {
        query = passedFilterQuery + ' AND ' + query;
      } else {
        query = passedFilterQuery;
      }
    }

    // DASH-9694: For comparison section filters we must force apply filter when analyticItem is documents.
    // This will add analyses.flatSections.[property]:[value] to the query if comparison filters are applied.
    const forceApplyComparisonFilter = widgetQueryOptions?.analyticItem === 'documents';

    const filterQuery = this.buildFilterString(otherFilters, true, forceApplyComparisonFilter);
    if (filterQuery) {
      if (query.trim().length > 0 && !query.includes('*:*')) {
        query = filterQuery + ' AND ' + query;
      } else {
        query = filterQuery;
      }
    }

    //Wrap the query with the analysisName. This causes the proper nesting to occur.
    if (analysisId) {
      query = `(analyses.analysisName: "${analysisId}"${query ? ' AND ' + query : ''} IN analyses)`;
    }

    return `${query}`;
  }

  public getAnalyticItemSectionInfoListQueryPart(
    analyticItem: string,
    selectedSections: SelectedSection[],
    config: FilterConfig
  ): string {
    // Handle section properties - use flat sections instead of sectionInfoList
    if (analyticItem?.startsWith('sectionProperties.')) {
      return this.getGlobalSectionListQueryPart(selectedSections);
    }

    let item = `analyses.${analyticItem}`;
    if (analyticItem === 'taxonomy') {
      item += '.taxonomyElements';
    }
    return this.getSectionInfoListQueryPart(item, selectedSections, config);
  }

  private getSectionInfoListQueryPart(
    item: string,
    selectedSections: SelectedSection[],
    config: FilterConfig
  ): string {
    // Handle section properties - use flat sections instead of sectionInfoList
    if (item?.includes('sectionProperties.') || item?.includes('flatSections')) {
      return this.getGlobalSectionListQueryPart(selectedSections);
    }

    const sectionInfoList = `${item}.sectionInfoList`;

    const buckets = selectedSections
      .map((s, _, arr) => {
        const negated = !!s.negated;
        const joinType = FilterUtils.sectionFilterBucketJoinType(s);
        const prefix = FilterUtils.sectionFilterPrefix(s);
        const field = `${sectionInfoList}.${this.getSectionFilterFieldName(s)}`;
        const bucketString = FilterUtils.computeSelectedSectionBuckets(field, s, config)
          .map((b) => FilterUtils.wrapWithParentheses(`${prefix}${field}:${b}`, negated))
          .join(` ${joinType} `);
        return arr.length > 1 ? `(${bucketString})` : bucketString;
      })
      .join(' AND ');

    return `(${buckets} IN ${sectionInfoList})`;
  }

  private getFlatSectionsSentimentQueryPart(
    selectedSections: SelectedSection[],
    config: FilterConfig,
    sentimentValue: any,
    sentimentScale: number
  ): string {
    return this.getFlatSectionsQueryPart(
      selectedSections,
      (bucket, field, prefix, negated) => {
        const bucketValue = FilterUtils.wrapWithParentheses(`${prefix}${field}:${bucket}`, negated);
        return `(${bucketValue} AND analyses.flatSections.sentimentPolarity${sentimentScale}:${sentimentValue})`;
      },
      config
    );
  }

  private getSelectedSectionsFromWidgetQueryOptions(
    widgetQueryOptions?: WidgetQueryOptions
  ): SelectedSection[] {
    if (widgetQueryOptions?.sectionFilter) {
      return widgetQueryOptions.sectionFilter;
    }
    return [];
  }

  private getSelectedSectionsFromFilter(filter: Filter): SelectedSection[] {
    if (filter?.selectedSections) {
      return filter.selectedSections;
    }

    // Backward compatibility
    if (filter?.sectionField && filter.sectionField !== Constants.ALL_SECTION_ID) {
      return [
        {
          field: filter.sectionField,
          buckets: filter.sectionBuckets ?? [],
        },
      ];
    }

    return [];
  }

  public getGlobalSectionListQueryPart(
    selectedSections: SelectedSection[],
    userSearchString?: string
  ): string {
    return this.getFlatSectionsQueryPart(
      selectedSections,
      (bucket, field, prefix, negated) =>
        FilterUtils.wrapWithParentheses(`${prefix}${field}:${bucket}`, negated),
      null,
      userSearchString
    );
  }

  private getFlatSectionsQueryPart(
    selectedSections: SelectedSection[],
    sectionBucketMapper: (
      bucket: string,
      field: string,
      prefix: string,
      negated: boolean
    ) => string,
    config: FilterConfig,
    userSearchString?: string
  ): string {
    const flatSectionList = `analyses.flatSections`;
    const buckets = selectedSections
      .map((s, _, arr) => {
        const negated = !!s.negated;
        const joinType = FilterUtils.sectionFilterBucketJoinType(s);
        const prefix = FilterUtils.sectionFilterPrefix(s);
        const field = `${flatSectionList}.${this.getSectionFilterFieldName(s, { metadata: 'metadata.' })}`;
        const bucketString = FilterUtils.computeSelectedSectionBuckets(field, s, config)
          .map((bucket) => sectionBucketMapper(bucket, field, prefix, negated))
          .join(` ${joinType} `);
        // Wrap in parentheses if there are multiple section properties OR multiple buckets
        return arr.length > 1 || s.buckets.length > 1 ? `(${bucketString})` : bucketString;
      })
      .join(' AND ');

    if (userSearchString) {
      return `((${buckets}) AND ${userSearchString} IN ${flatSectionList})`;
    }

    return `(${buckets} IN ${flatSectionList})`;
  }

  private getSectionFilterFieldName(
    section: SelectedSection,
    prefixProvider?: Partial<Record<SectionMetadataType, string>>
  ) {
    const sectionType: SectionMetadataType =
      section.type || Constants.DEFAULT_SECTION_METADATA_TYPE;
    let filterName: string = section.field;
    if (sectionType === 'property') {
      filterName = section.field.replace(Constants.SECTION_PROPERTIES_PREFIX, '');
    }
    const prefix: string = prefixProvider?.[sectionType] ?? '';
    return `${prefix}${filterName}`;
  }

  //Default vaulues in case date range charts only have one entry.
  //Using a 31 day month because if the chart only has one point anyway this is fine to assume.
  public static defaultDateIntervalStep(dateInterval: string) {
    switch (dateInterval) {
      case 'minute':
        return 60000;
      case 'hour':
        return 3600000;
      case 'day':
        return 86400000;
      case 'week':
        return 604800000;
      case 'month':
        return 2678400000;
      case 'quarter':
        return 7948800000;
      case 'year':
        return 31536000000;
      default:
        return 86400000;
    }
  }

  computeFixedValueForDateFilter(key: string, value?: string): [string, string] {
    if (key === 'daterange') {
      return this.computeDaterangeFixedValueForDateFilter(value);
    }

    return this.dateRangeService.computeRelativeFixedValueForDateFilter(key);
  }

  private computeDaterangeFixedValueForDateFilter(value: string): [string, string] {
    const parts = value.split('/');
    return [parts[1], parts[2]];
  }

  computeLabelForDateFilter(value: string, formatType?): string {
    const parts = value.split('/');

    if (parts.length === 1) {
      return this.getFilterConfigItem('createdAt').choices.find((c) => c.key === value)?.label;
    }

    let labelFrom = parts[1];
    let labelTo = parts[2];

    if (labelFrom === '*') {
      labelFrom = 'whenever';
    } else {
      labelFrom = this.lexDatePipe.transform(labelFrom, formatType);
    }

    if (labelTo === '*') {
      labelTo = 'whenever';
    } else {
      labelTo = this.lexDatePipe.transform(labelTo, formatType);
    }

    return `${labelFrom} to ${labelTo}`;
  }

  public generateRelativeRangeOptions() {
    const options = dateFilterChoices
      .filter((filter) => filter.key !== 'daterange')
      .map((filter) => ({
        id: filter.key,
        title: filter.label,
        first: undefined,
        last: undefined,
      }));

    for (const opt of options) {
      const [first, last] = this.computeFixedValueForDateFilter(opt.id);
      let formattedLast = last;
      //Subtract 1 ms to ensure correct display in the Calendar picker
      if (!opt.id.includes('IT') && last.endsWith('000')) {
        const tempLast = moment(last)
          .subtract(1, 'millisecond')
          .format(DATE_TIME_WITHOUT_TIMEZONE_FORMAT);
        //If now is the first of the month, ensure correct display for mtd & ytd
        if (moment(first).isBefore(moment(tempLast))) {
          formattedLast = tempLast;
        }
      }

      opt.first = new Date(first);
      opt.last = new Date(formattedLast);
    }

    return options;
  }

  public getWidgetQueryOptions(
    widget: Widget<any>,
    dataSource: WidgetDataSource,
    widgetGroup: WidgetGroup,
    comparisonFilterSet?: FilterSet
  ): WidgetQueryOptions {
    const optionFilters = widget.payload.filters
      ? JSON.parse(JSON.stringify(widget.payload.filters))
      : FilterService.getDefaultFilterSet();

    if (widget.id !== 'dummy') {
      if (widgetGroup) {
        Object.keys(widgetGroup.filters).forEach((key) => {
          optionFilters[key].and.push(...widgetGroup.filters[key].and);
          optionFilters[key].or.push(...widgetGroup.filters[key].or);
          optionFilters[key].not.push(...widgetGroup.filters[key].not);
        });

        if (widgetGroup.parentWidget && widgetGroup.parentWidget !== widget.id) {
          Object.keys(widgetGroup.parentFilters).forEach((key) =>
            optionFilters[key].and.push(...widgetGroup.parentFilters[key].and)
          );
        }
      }

      if (comparisonFilterSet) {
        for (const operator of ['and', 'or', 'not']) {
          comparisonFilterSet[operator].forEach((filter: Filter) => {
            const isComparisonSectionFilter = FilterUtils.isComparisonSectionFilter(filter);
            let filterSet: 'dates' | 'sectionProperties' | 'metadata' = 'metadata';
            if (filter.analyticItem === 'createdAt') {
              filterSet = 'dates';
            } else if (isComparisonSectionFilter) {
              filterSet = 'sectionProperties';
            }
            optionFilters[filterSet][operator].push(filter);
          });
        }
      }
    }

    let boxScoringQueryOptions: BoxScoringQueryOptions;
    if (widget.payload.type === 'donut' && widget.payload.donutOptions.scoring === 'box') {
      const donutOptions = (widget.payload as WidgetChartOptions).donutOptions;

      boxScoringQueryOptions = {
        fieldId: widget.payload.analyticItem,
        min: donutOptions.minRating,
        max: donutOptions.maxRating,
        topBoxSize: donutOptions.topBoxSize,
        bottomBoxSize: donutOptions.bottomBoxSize,
        type: 'donut',
      };
    } else if (
      widget.payload.type === 'breakdown' &&
      widget.payload.breakdownOptions.scoring === 'box'
    ) {
      const breakdownOptions = (widget.payload as WidgetChartOptions).breakdownOptions;

      boxScoringQueryOptions = {
        fieldId: breakdownOptions.numericMetadataFieldId,
        min: breakdownOptions.min,
        max: breakdownOptions.max,
        topBoxSize: breakdownOptions.topBoxSize,
        bottomBoxSize: breakdownOptions.bottomBoxSize,
        type: 'breakdown',
      };
    }

    return {
      analyticItem: widget.payload.analyticItem,
      subType: widget.payload.entityTypeFilter,
      type: widget.payload.type,
      drilledDownOn: widget.payload.drilledDownOn,
      drilledDownLocation: widget.payload.drilledDownLocation,
      taxonomyMentionField: widget.payload.taxonomyMentionField,
      boxScoringQueryOptions,
      widgetDataSource: dataSource,
      customFieldSelected: widget.payload.customFieldSelected,
      standardFieldSelected: widget.payload.standardFieldSelected,
      sectionPropertySelected: widget.payload.sectionPropertySelected,
      filters: optionFilters,
      sentimentScale: widget.payload.sentimentScale,
      selectedItems: widget.payload.selectedItems,
      dateInterval: widget.payload.dateInterval,
      userQuery: widget.payload.userQuery,
      subject: widget.payload.subject,
      subjectValue: widget.payload.subjectValue,
      selectedSection: widget.payload?.selectedSection,
      userSearch: widget.payload.userSearch,
      sectionFilter: widget.payload.sectionFilter,
      contentChannelFilter: widget.payload.contentChannelFilter,
    };
  }

  public getWidgetDateFilters(
    dateFilterType: 'previous' | 'current' | 'both',
    previousStart: Moment,
    previousEnd: Moment,
    currentStart: Moment,
    currentEnd: Moment
  ): FilterSet {
    const dateFilters: FilterSet = {
      not: [],
      or: [],
      and: [],
    };

    switch (dateFilterType) {
      case 'both':
      default: {
        const previousRange = this.createDaterangeFilterFromPeriod(previousStart, previousEnd);
        const currentRange = this.createDaterangeFilterFromPeriod(currentStart, currentEnd);

        dateFilters.or.push(previousRange, currentRange);

        break;
      }
      case 'previous': {
        const previousRange = this.createDaterangeFilterFromPeriod(previousStart, previousEnd);

        dateFilters.and.push(previousRange);

        break;
      }
      case 'current': {
        const currentRange = this.createDaterangeFilterFromPeriod(currentStart, currentEnd);

        dateFilters.and.push(currentRange);

        break;
      }
    }

    return dateFilters;
  }

  createDaterangeFilterFromPeriod(startPeriod: string | Moment, endPeriod: string | Moment) {
    let rangeFromPeriod = startPeriod as Moment;
    let rangeToPeriod = endPeriod as Moment;
    if (typeof startPeriod === 'string' && typeof endPeriod === 'string') {
      rangeFromPeriod = moment(startPeriod) as Moment;
      rangeToPeriod = moment(endPeriod) as Moment;
      if (startPeriod.endsWith('Z')) {
        rangeFromPeriod = rangeFromPeriod.tz(this.timezoneService.timezone, true);
      }
      if (endPeriod.endsWith('Z')) {
        rangeToPeriod = rangeToPeriod.tz(this.timezoneService.timezone, true);
      }
    }
    //Get beginning of first day of range, end of last day
    const rangeFrom = rangeFromPeriod?.startOf('day').format(DATE_TIME_WITHOUT_TIMEZONE_FORMAT);
    const rangeTo = rangeToPeriod?.endOf('day').format(DATE_TIME_WITHOUT_TIMEZONE_FORMAT);
    return {
      analyticItem: 'createdAt',
      key: 'daterange',
      value: `daterange/${rangeFrom}/${rangeTo}`,
    };
  }

  toMomentIsoString(millis: number) {
    return moment.tz(millis, this.timezoneService.timezone).toISOString();
  }
}
